class Cart {
	constructor() {
		this.items = [];
		this.itemsMarkup = '';
		this.cartItemsMarkup = '';
		this.cartSummaryMarkup = '';
		this.cartContainerElement = document.querySelector('cart-component');
		this.cartSummaryElement = this.cartContainerElement?.querySelector('.cart-summary');
		this.sideCartToggle = document.querySelector('[data-ref="cartToggle"]');
		this.grapheneRecommendationsId = this.cartContainerElement?.getAttribute('data-graphene-recommendations');
	}

	connectedCallback() {
		//
	}

	dispatchCartUpdated() {
		const totalCount = this.items.reduce(
			(sum, item) => sum + item.quantity,
			0
		);
		const event = new CustomEvent('cart:updated', {
			detail: { totalCount, items: this.items }
		});
		document.dispatchEvent(event);
	}

	// Fetch the current cart from Shopify and update local items
	async fetchCart() {
		try {
			const cartData = await fetch('/cart.js').then((res) => res.json());
			this.items = cartData.items || [];
			const response = await fetch('/?sections=cart');
			const data = await response.json();
			this.itemsMarkup = data.cart ? data.cart : '';
			// Get specific parts of the cart markup
			const cartElement = document.createElement('div');
			cartElement.innerHTML = this.itemsMarkup;
			const cartItems = cartElement.querySelector('.cart');
			const cartSummary = cartElement.querySelector('.cart-summary');
			this.cartItemsMarkup = cartItems ? cartItems.innerHTML : '';
			this.cartSummaryMarkup = cartSummary ? cartSummary.innerHTML : '';
			this.renderCartItems();
			this.renderCartSummary();
			this.renderCartRecommendations();
			// Dispatch event
			this.dispatchCartUpdated();
			return this.items;
		} catch (error) {
			console.error('Error fetching cart:', error);
			throw error; // Re-throw to handle it in the calling code
		}
	}

	normalizeCartResponse(response) {
		const data = response._parsedJson || null;
		const message = data?.description || data?.error || data?.message || response.statusText || `HTTP ${response.status}`;
		const parsedStatus = Number(data?.status);
		const status = Number.isFinite(parsedStatus)
			? parsedStatus
			: response.ok
			? response.status
			: 422;

		if (!response.ok || (data && (data.status === 'error' || data.error || data.description))) {
			return {
				ok: false,
				status,
				message,
				data
			};
		}
		return {
			ok: true,
			status: response.status,
			message: 'OK',
			data
		};
	}

	renderCartItems() {
		this.cartContainerElement = document.querySelector('.cart');
		if (this.cartContainerElement) {
			this.cartContainerElement.innerHTML = this.cartItemsMarkup;
			this.sideCartToggle = document.querySelector('[data-ref="cartToggle"]');
			if (document.body.getAttribute('data-template') != 'cart') {
				this.sideCartToggle.checked = true;
			} else {
				this.sideCartToggle.checked = false;
			}
		}
	}

	renderCartSummary() {
		if (this.cartSummaryElement) {
			this.cartSummaryElement.innerHTML = this.cartSummaryMarkup;
		}
	}

	renderCartRecommendations() {
		const grapheneRecommendationsElement = this.cartContainerElement?.querySelector('[data-graphene-recommendations-id]');
		const grapheneRecommendationsId = grapheneRecommendationsElement?.getAttribute('data-graphene-recommendations-id');
		const grapheneRecommendationsTarget = grapheneRecommendationsElement?.getAttribute('data-target');
		if (grapheneRecommendationsId) {
			window.$g.load({
				elements: [
					{
						id: grapheneRecommendationsId,
						target: grapheneRecommendationsTarget
					}
				]
			});
		}
	}

	parsePreorderDate(value) {
		if (!value || typeof value !== 'string') return null;
		const normalized = value
			.trim()
			.replace(/(\d)(st|nd|rd|th)/gi, '$1')
			.replace(/\s+/g, ' ');
		const parsedDate = new Date(normalized);
		return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
	}

	getPreorderAttributeValue(items = []) {
		const cartItems = [...this.items, ...items].filter(Boolean);
		const preorderDates = cartItems
			.map((item) => item?.properties?.Preorder || item?.properties?.['Preorder'])
			.filter((value) => typeof value === 'string' && value.trim())
			.map((value) => ({
				value,
				date: this.parsePreorderDate(value)
			}))
			.filter(({ date }) => date !== null);

		if (!preorderDates.length) {
			return 'false';
		}

		const furthestPreorder = preorderDates.reduce((latest, current) => {
			if (!latest) return current;
			return current.date > latest.date ? current : latest;
		}, null);

		return furthestPreorder ? furthestPreorder.value : 'false';
	}

	getMultiplePreordersAttributeValue(items = []) {
		const cartItems = [...this.items, ...items].filter(Boolean);
		const preorderCount = cartItems.filter((item) => {
			const value = item?.properties?.Preorder || item?.properties?.['Preorder'];
			return typeof value === 'string' && value.trim().length > 0;
		}).length;

		return preorderCount > 1 ? 'true' : 'false';
	}

	async setPreorderCartAttribute() {
		const preorderDate = this.getPreorderAttributeValue();
		const multiplePreorders = this.getMultiplePreordersAttributeValue();
		try {
			const response = await fetch('/cart/update.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					attributes: {
						Preorder: preorderDate,
						'Multiple preorders': multiplePreorders
					}
				})
			});

			response._parsedJson = await response.json().catch(() => null);
			return this.normalizeCartResponse(response);
		} catch (error) {
			return { ok: false, status: 0, message: error.message || 'Network error', data: null };
		}
	}

	// Add one or more items to the cart
	async addItems(items) {
		const preorderAttributeValue = this.getPreorderAttributeValue(items);
		const multiplePreordersAttributeValue = this.getMultiplePreordersAttributeValue(items);
		console.log('Adding items to cart:', items, 'Preorder date:', preorderAttributeValue, 'Multiple preorders:', multiplePreordersAttributeValue);
		try {
			const response = await fetch('/cart/add.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					items: items.map((item) => ({
						id: item.id,
						quantity: item.quantity,
						selling_plan_id: null,
						properties: item.properties
					})),
					attributes: {
						Preorder: preorderAttributeValue,
						'Multiple preorders': multiplePreordersAttributeValue
					}
				})
			});

			response._parsedJson = await response.json().catch(() => null);
			const result = this.normalizeCartResponse(response);

			if (!result.ok) {
				return result;
			}

			await this.fetchCart();
			await this.setPreorderCartAttribute();
			return result;
		} catch (error) {
			return { ok: false, status: 0, message: error.message || 'Network error', data: null };
		}
	}

	// Update the quantity of one or more items in the cart
	async updateItems(items) {
		const preorderAttributeValue = this.getPreorderAttributeValue(items);
		const multiplePreordersAttributeValue = this.getMultiplePreordersAttributeValue(items);
		try {
			const updates = items.reduce((acc, item) => {
				acc[item.id] = item.quantity;
				return acc;
			}, {});

			const response = await fetch('/cart/update.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					updates,
					attributes: {
						Preorder: preorderAttributeValue,
						'Multiple preorders': multiplePreordersAttributeValue
					}
				})
			});

			response._parsedJson = await response.json().catch(() => null);
			const result = this.normalizeCartResponse(response);

			if (!result.ok) {
				return result;
			}

			await this.fetchCart();
			await this.renderCartItems();	
			await this.setPreorderCartAttribute();
			return result;
		} catch (error) {
			return { ok: false, status: 0, message: error.message || 'Network error', data: null };
		}
	}

	// Remove an item from the cart (set quantity to 0)
	async removeItem(id) {
		return this.updateItems([{ id, quantity: 0 }]);
	}

	// Clear the entire cart
	async clearCart() {
		try {
			const response = await fetch('/cart/clear.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' }
			});

			response._parsedJson = await response.json().catch(() => null);
			const result = this.normalizeCartResponse(response);

			if (!result.ok) {
				return result;
			}

			await this.fetchCart();
			return result;
		} catch (error) {
			return { ok: false, status: 0, message: error.message || 'Network error', data: null };
		}
	}

	// Get the current items (fetches latest from Shopify)
	async getItems() {
		return await this.fetchCart();
	}

}

function formatMoney (cents, format) {
  const money_format = document.body.getAttribute('data-currency-format') || '£{{amount}}';
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || money_format;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

class ProductForm extends HTMLElement {
	constructor() {
		super();
		this.cart = new Cart();
		this.handleSubmitEvent = this.handleSubmit.bind(this);
		this.errors = this.querySelector('[data-ref="errors"]');
		this.quantitySelector = this.querySelector('quantity-selector');
		this.productPrice = this.querySelector('product-price');

		this.products = JSON.parse(this.getAttribute('products'));

		this.totalSetPriceElement = this.querySelector('[data-ref="totalSetPrice"]');

	}

	connectedCallback() {
		const form = this.querySelector('form');
		if (form) {
			form.addEventListener('submit', this.handleSubmitEvent);
		} else {
			console.warn('No <form> found inside <product-form>');
		}

		if (this.querySelector('product-set-item')) {
			this.addEventListener('product-set-item-changed', (event) => {

				console.log(event.detail);

				const matchingProduct = this.products.find((product) => {
					return product.id == event.detail.id;
				});

				if (matchingProduct) {
					this.products = this.products.map((product) => {
						return product == matchingProduct ? event.detail : product;
					});
				}
				this.setAttribute('products', JSON.stringify(this.products));
					
				const totalSetPrice = this.products.reduce((a, b) => +a + (+b.price * b.quantity), 0);

				console.log(totalSetPrice);
				console.log(this.products);

				if (this.totalSetPriceElement) {
					this.totalSetPriceElement.innerText = `${formatMoney(totalSetPrice)}`;
				}

			});
		}
	}

	disconnectedCallback() {
		const form = this.querySelector('form');
		if (form) {
			form.removeEventListener('submit', this.handleSubmitEvent);
		}
	}

	async handleSubmit(event) {
		event.preventDefault();

		let items = [];
		
		if (this.querySelector('[data-ref="productSetItem"]')) {
			const fields = this.querySelectorAll('[data-ref="productSetItem"]');
			items = [...fields]
				.map((field) => {
					const id = Number.parseInt(field.querySelector('[name$="[id]"]').value, 10);
					const quantity = Number.parseInt(field.querySelector('[name$="[quantity]"]').value, 10) || 0;
					return { id, quantity };
				})
				.filter((item) => Number.isInteger(item.id) && item.id > 0 && item.quantity > 0);

			if (!items.length) {
				console.error('No valid product items found');
				return;
			}

		} else {

			const formData = new FormData(event.target);
			const id = Number.parseInt(formData.get('id'), 10);
			const quantity = Number.parseInt(formData.get('quantity'), 10) || 1;

			const properties = [...formData.entries()]
				.filter(([name]) => /^properties\[[^\]]+\]$/.test(name))
				.reduce((acc, [name, value]) => {
					const key = name.match(/^properties\[([^\]]+)\]$/)[1];
					acc[key] = value;
					return acc;
				}, {});

			if (!Number.isInteger(id) || id <= 0) {
				console.error('Product ID is required');
				return;
			}
			items = [{ id, quantity, properties }];
		}

		const result = await this.cart.addItems(items);
		if (result && typeof result === 'object' && result.ok === false) {
			console.error('Error adding to cart:', result.message);
			this.displayError(result);
			return result;
		}
		
	}

	displayError(error) {
		if (!this.errors) {
			console.error('Failed to show fallback alert for error:', error.message || error);
			return;
		}

		const msg =
			error && typeof error.message === 'string'
				? error.message
				: typeof error === 'string'
				? error
				: String(error);

		this.errors.innerText = msg;
		this.errors.classList.remove('hidden');
		setTimeout(() => {
			this.errors.classList.add('hidden');
			this.errors.innerText = '';
		}, 2000);
	}
}

class CartItem extends HTMLElement {
	constructor() {
		super();
		this.quantitySelector = this.querySelector('[data-ref="quantitySelector"]');
		this.cartRemoveButton = this.querySelector('[data-ref="cartRemoveButton"]');
		this.cart = new Cart();

    // Store handlers so they can be removed later
    this._updateHandler = (event) => {
      const id = this.dataset.variantId;
      const quantity = this.quantitySelector.quantity || 1;
      this.cart.updateItems([{ id, quantity }]);
    };

    this._removeHandler = (event) => {
      const id = this.dataset.variantId;
      this.cart.removeItem(id);
    };
	}

	connectedCallback() {
		if (this.quantitySelector) {
			this.quantitySelector.addEventListener('quantity-changed', this._updateHandler);
		}
		if (this.cartRemoveButton) {
			this.cartRemoveButton.addEventListener('click', this._removeHandler);
		}
	}

	disconnectedCallback() {
		if (this.quantitySelector) {
			this.quantitySelector.removeEventListener('quantity-changed', this._updateHandler);
		}
		if (this.cartRemoveButton) {
			this.cartRemoveButton.removeEventListener('click', this._removeHandler);
		}
	}
}

class CartCount extends HTMLElement {
	constructor() {
		super();
		this.count = this.querySelector('[data-ref="count"]');
		this.updateEventHandler = this.updateCount.bind(this);
	}

	connectedCallback() {
		document.addEventListener('cart:updated', this.updateEventHandler);
	}

	disconnectedCallback() {
		document.removeEventListener('cart:updated', this.updateEventHandler);
	}

	updateCount(event) {
		const total = event.detail.totalCount;
		if (this.count) {
			this.count.textContent = String(total);
		}
		if (total > 0) {
			this.count.classList.remove('hidden');
		} else {
			this.count.classList.add('hidden');
		}
	}

}

class CountdownTimer extends HTMLElement {
	constructor() {
		super();
    this.days = 0;
    this.hours = 0;
    this.minutes = 0;
    this.seconds = 0;
    
    this.endTime = this.getAttribute('end-time');
	}

	connectedCallback() {
    // Define timer elements
		this.daysElement = this.querySelector('[data-ref="days"]');
		this.hoursElement = this.querySelector('[data-ref="hours"]');
		this.minutesElement = this.querySelector('[data-ref="minutes"]');
		this.secondsElement = this.querySelector('[data-ref="seconds"]');
    // Set initial values
		this.update();
    // Run every second
    this._intervalId = window.setInterval(() => this.update(), 1000);
  }

  disconnectedCallback() {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }

  calculateTime() {
    // Create two Date instances
    let startTime = new Date();  // Current time
    let endTime = new Date(`${this.endTime}`);  // Year, Month, Day, Hour, Minute, Second

    // Calculate the difference in milliseconds
    let differenceInMs = endTime - startTime;

    // Convert to total seconds
    let totalSeconds = Math.floor(differenceInMs / 1000);

    // Calculate remaining hours, minutes, and seconds
    this.days = Math.floor(totalSeconds / 86400);
    this.hours = Math.floor((totalSeconds % 86400) / 3600);
    this.minutes = Math.floor((totalSeconds % 3600) / 60);
    this.seconds = totalSeconds % 60;
  }

  update() {
    // Recalculate time and update countdown elements
    this.calculateTime();
    if (this.daysElement) {
      this.daysElement.textContent = String(this.days);
      this.daysElement.setAttribute('style', `--value:${this.days}`);
    }
    if (this.hoursElement) {
      this.hoursElement.textContent = String(this.hours);
      this.hoursElement.setAttribute('style', `--value:${this.hours}`);
    }
    if (this.minutesElement) {
      this.minutesElement.textContent = String(this.minutes);
      this.minutesElement.setAttribute('style', `--value:${this.minutes}`);
    }
    if (this.secondsElement) {
      this.secondsElement.textContent = String(this.seconds);
      this.secondsElement.setAttribute('style', `--value:${this.seconds}`);
    }
  }
}

function isNumber(subject) {
  return typeof subject === 'number';
}
function isString(subject) {
  return typeof subject === 'string';
}
function isBoolean(subject) {
  return typeof subject === 'boolean';
}
function isObject(subject) {
  return Object.prototype.toString.call(subject) === '[object Object]';
}
function mathAbs(n) {
  return Math.abs(n);
}
function mathSign(n) {
  return Math.sign(n);
}
function deltaAbs(valueB, valueA) {
  return mathAbs(valueB - valueA);
}
function factorAbs(valueB, valueA) {
  if (valueB === 0 || valueA === 0) return 0;
  if (mathAbs(valueB) <= mathAbs(valueA)) return 0;
  const diff = deltaAbs(mathAbs(valueB), mathAbs(valueA));
  return mathAbs(diff / valueB);
}
function roundToTwoDecimals(num) {
  return Math.round(num * 100) / 100;
}
function arrayKeys(array) {
  return objectKeys(array).map(Number);
}
function arrayLast(array) {
  return array[arrayLastIndex(array)];
}
function arrayLastIndex(array) {
  return Math.max(0, array.length - 1);
}
function arrayIsLastIndex(array, index) {
  return index === arrayLastIndex(array);
}
function arrayFromNumber(n, startAt = 0) {
  return Array.from(Array(n), (_, i) => startAt + i);
}
function objectKeys(object) {
  return Object.keys(object);
}
function objectsMergeDeep(objectA, objectB) {
  return [objectA, objectB].reduce((mergedObjects, currentObject) => {
    objectKeys(currentObject).forEach(key => {
      const valueA = mergedObjects[key];
      const valueB = currentObject[key];
      const areObjects = isObject(valueA) && isObject(valueB);
      mergedObjects[key] = areObjects ? objectsMergeDeep(valueA, valueB) : valueB;
    });
    return mergedObjects;
  }, {});
}
function isMouseEvent(evt, ownerWindow) {
  return typeof ownerWindow.MouseEvent !== 'undefined' && evt instanceof ownerWindow.MouseEvent;
}

function Alignment(align, viewSize) {
  const predefined = {
    start,
    center,
    end
  };
  function start() {
    return 0;
  }
  function center(n) {
    return end(n) / 2;
  }
  function end(n) {
    return viewSize - n;
  }
  function measure(n, index) {
    if (isString(align)) return predefined[align](n);
    return align(viewSize, n, index);
  }
  const self = {
    measure
  };
  return self;
}

function EventStore() {
  let listeners = [];
  function add(node, type, handler, options = {
    passive: true
  }) {
    let removeListener;
    if ('addEventListener' in node) {
      node.addEventListener(type, handler, options);
      removeListener = () => node.removeEventListener(type, handler, options);
    } else {
      const legacyMediaQueryList = node;
      legacyMediaQueryList.addListener(handler);
      removeListener = () => legacyMediaQueryList.removeListener(handler);
    }
    listeners.push(removeListener);
    return self;
  }
  function clear() {
    listeners = listeners.filter(remove => remove());
  }
  const self = {
    add,
    clear
  };
  return self;
}

function Animations(ownerDocument, ownerWindow, update, render) {
  const documentVisibleHandler = EventStore();
  const fixedTimeStep = 1000 / 60;
  let lastTimeStamp = null;
  let accumulatedTime = 0;
  let animationId = 0;
  function init() {
    documentVisibleHandler.add(ownerDocument, 'visibilitychange', () => {
      if (ownerDocument.hidden) reset();
    });
  }
  function destroy() {
    stop();
    documentVisibleHandler.clear();
  }
  function animate(timeStamp) {
    if (!animationId) return;
    if (!lastTimeStamp) {
      lastTimeStamp = timeStamp;
      update();
      update();
    }
    const timeElapsed = timeStamp - lastTimeStamp;
    lastTimeStamp = timeStamp;
    accumulatedTime += timeElapsed;
    while (accumulatedTime >= fixedTimeStep) {
      update();
      accumulatedTime -= fixedTimeStep;
    }
    const alpha = accumulatedTime / fixedTimeStep;
    render(alpha);
    if (animationId) {
      animationId = ownerWindow.requestAnimationFrame(animate);
    }
  }
  function start() {
    if (animationId) return;
    animationId = ownerWindow.requestAnimationFrame(animate);
  }
  function stop() {
    ownerWindow.cancelAnimationFrame(animationId);
    lastTimeStamp = null;
    accumulatedTime = 0;
    animationId = 0;
  }
  function reset() {
    lastTimeStamp = null;
    accumulatedTime = 0;
  }
  const self = {
    init,
    destroy,
    start,
    stop,
    update,
    render
  };
  return self;
}

function Axis(axis, contentDirection) {
  const isRightToLeft = contentDirection === 'rtl';
  const isVertical = axis === 'y';
  const scroll = isVertical ? 'y' : 'x';
  const cross = isVertical ? 'x' : 'y';
  const sign = !isVertical && isRightToLeft ? -1 : 1;
  const startEdge = getStartEdge();
  const endEdge = getEndEdge();
  function measureSize(nodeRect) {
    const {
      height,
      width
    } = nodeRect;
    return isVertical ? height : width;
  }
  function getStartEdge() {
    if (isVertical) return 'top';
    return isRightToLeft ? 'right' : 'left';
  }
  function getEndEdge() {
    if (isVertical) return 'bottom';
    return isRightToLeft ? 'left' : 'right';
  }
  function direction(n) {
    return n * sign;
  }
  const self = {
    scroll,
    cross,
    startEdge,
    endEdge,
    measureSize,
    direction
  };
  return self;
}

function Limit(min = 0, max = 0) {
  const length = mathAbs(min - max);
  function reachedMin(n) {
    return n < min;
  }
  function reachedMax(n) {
    return n > max;
  }
  function reachedAny(n) {
    return reachedMin(n) || reachedMax(n);
  }
  function constrain(n) {
    if (!reachedAny(n)) return n;
    return reachedMin(n) ? min : max;
  }
  function removeOffset(n) {
    if (!length) return n;
    return n - length * Math.ceil((n - max) / length);
  }
  const self = {
    length,
    max,
    min,
    constrain,
    reachedAny,
    reachedMax,
    reachedMin,
    removeOffset
  };
  return self;
}

function Counter(max, start, loop) {
  const {
    constrain
  } = Limit(0, max);
  const loopEnd = max + 1;
  let counter = withinLimit(start);
  function withinLimit(n) {
    return !loop ? constrain(n) : mathAbs((loopEnd + n) % loopEnd);
  }
  function get() {
    return counter;
  }
  function set(n) {
    counter = withinLimit(n);
    return self;
  }
  function add(n) {
    return clone().set(get() + n);
  }
  function clone() {
    return Counter(max, get(), loop);
  }
  const self = {
    get,
    set,
    add,
    clone
  };
  return self;
}

function DragHandler(axis, rootNode, ownerDocument, ownerWindow, target, dragTracker, location, animation, scrollTo, scrollBody, scrollTarget, index, eventHandler, percentOfView, dragFree, dragThreshold, skipSnaps, baseFriction, watchDrag) {
  const {
    cross: crossAxis,
    direction
  } = axis;
  const focusNodes = ['INPUT', 'SELECT', 'TEXTAREA'];
  const nonPassiveEvent = {
    passive: false
  };
  const initEvents = EventStore();
  const dragEvents = EventStore();
  const goToNextThreshold = Limit(50, 225).constrain(percentOfView.measure(20));
  const snapForceBoost = {
    mouse: 300,
    touch: 400
  };
  const freeForceBoost = {
    mouse: 500,
    touch: 600
  };
  const baseSpeed = dragFree ? 43 : 25;
  let isMoving = false;
  let startScroll = 0;
  let startCross = 0;
  let pointerIsDown = false;
  let preventScroll = false;
  let preventClick = false;
  let isMouse = false;
  function init(emblaApi) {
    if (!watchDrag) return;
    function downIfAllowed(evt) {
      if (isBoolean(watchDrag) || watchDrag(emblaApi, evt)) down(evt);
    }
    const node = rootNode;
    initEvents.add(node, 'dragstart', evt => evt.preventDefault(), nonPassiveEvent).add(node, 'touchmove', () => undefined, nonPassiveEvent).add(node, 'touchend', () => undefined).add(node, 'touchstart', downIfAllowed).add(node, 'mousedown', downIfAllowed).add(node, 'touchcancel', up).add(node, 'contextmenu', up).add(node, 'click', click, true);
  }
  function destroy() {
    initEvents.clear();
    dragEvents.clear();
  }
  function addDragEvents() {
    const node = isMouse ? ownerDocument : rootNode;
    dragEvents.add(node, 'touchmove', move, nonPassiveEvent).add(node, 'touchend', up).add(node, 'mousemove', move, nonPassiveEvent).add(node, 'mouseup', up);
  }
  function isFocusNode(node) {
    const nodeName = node.nodeName || '';
    return focusNodes.includes(nodeName);
  }
  function forceBoost() {
    const boost = dragFree ? freeForceBoost : snapForceBoost;
    const type = isMouse ? 'mouse' : 'touch';
    return boost[type];
  }
  function allowedForce(force, targetChanged) {
    const next = index.add(mathSign(force) * -1);
    const baseForce = scrollTarget.byDistance(force, !dragFree).distance;
    if (dragFree || mathAbs(force) < goToNextThreshold) return baseForce;
    if (skipSnaps && targetChanged) return baseForce * 0.5;
    return scrollTarget.byIndex(next.get(), 0).distance;
  }
  function down(evt) {
    const isMouseEvt = isMouseEvent(evt, ownerWindow);
    isMouse = isMouseEvt;
    preventClick = dragFree && isMouseEvt && !evt.buttons && isMoving;
    isMoving = deltaAbs(target.get(), location.get()) >= 2;
    if (isMouseEvt && evt.button !== 0) return;
    if (isFocusNode(evt.target)) return;
    pointerIsDown = true;
    dragTracker.pointerDown(evt);
    scrollBody.useFriction(0).useDuration(0);
    target.set(location);
    addDragEvents();
    startScroll = dragTracker.readPoint(evt);
    startCross = dragTracker.readPoint(evt, crossAxis);
    eventHandler.emit('pointerDown');
  }
  function move(evt) {
    const isTouchEvt = !isMouseEvent(evt, ownerWindow);
    if (isTouchEvt && evt.touches.length >= 2) return up(evt);
    const lastScroll = dragTracker.readPoint(evt);
    const lastCross = dragTracker.readPoint(evt, crossAxis);
    const diffScroll = deltaAbs(lastScroll, startScroll);
    const diffCross = deltaAbs(lastCross, startCross);
    if (!preventScroll && !isMouse) {
      if (!evt.cancelable) return up(evt);
      preventScroll = diffScroll > diffCross;
      if (!preventScroll) return up(evt);
    }
    const diff = dragTracker.pointerMove(evt);
    if (diffScroll > dragThreshold) preventClick = true;
    scrollBody.useFriction(0.3).useDuration(0.75);
    animation.start();
    target.add(direction(diff));
    evt.preventDefault();
  }
  function up(evt) {
    const currentLocation = scrollTarget.byDistance(0, false);
    const targetChanged = currentLocation.index !== index.get();
    const rawForce = dragTracker.pointerUp(evt) * forceBoost();
    const force = allowedForce(direction(rawForce), targetChanged);
    const forceFactor = factorAbs(rawForce, force);
    const speed = baseSpeed - 10 * forceFactor;
    const friction = baseFriction + forceFactor / 50;
    preventScroll = false;
    pointerIsDown = false;
    dragEvents.clear();
    scrollBody.useDuration(speed).useFriction(friction);
    scrollTo.distance(force, !dragFree);
    isMouse = false;
    eventHandler.emit('pointerUp');
  }
  function click(evt) {
    if (preventClick) {
      evt.stopPropagation();
      evt.preventDefault();
      preventClick = false;
    }
  }
  function pointerDown() {
    return pointerIsDown;
  }
  const self = {
    init,
    destroy,
    pointerDown
  };
  return self;
}

function DragTracker(axis, ownerWindow) {
  const logInterval = 170;
  let startEvent;
  let lastEvent;
  function readTime(evt) {
    return evt.timeStamp;
  }
  function readPoint(evt, evtAxis) {
    const property = evtAxis || axis.scroll;
    const coord = `client${property === 'x' ? 'X' : 'Y'}`;
    return (isMouseEvent(evt, ownerWindow) ? evt : evt.touches[0])[coord];
  }
  function pointerDown(evt) {
    startEvent = evt;
    lastEvent = evt;
    return readPoint(evt);
  }
  function pointerMove(evt) {
    const diff = readPoint(evt) - readPoint(lastEvent);
    const expired = readTime(evt) - readTime(startEvent) > logInterval;
    lastEvent = evt;
    if (expired) startEvent = evt;
    return diff;
  }
  function pointerUp(evt) {
    if (!startEvent || !lastEvent) return 0;
    const diffDrag = readPoint(lastEvent) - readPoint(startEvent);
    const diffTime = readTime(evt) - readTime(startEvent);
    const expired = readTime(evt) - readTime(lastEvent) > logInterval;
    const force = diffDrag / diffTime;
    const isFlick = diffTime && !expired && mathAbs(force) > 0.1;
    return isFlick ? force : 0;
  }
  const self = {
    pointerDown,
    pointerMove,
    pointerUp,
    readPoint
  };
  return self;
}

function NodeRects() {
  function measure(node) {
    const {
      offsetTop,
      offsetLeft,
      offsetWidth,
      offsetHeight
    } = node;
    const offset = {
      top: offsetTop,
      right: offsetLeft + offsetWidth,
      bottom: offsetTop + offsetHeight,
      left: offsetLeft,
      width: offsetWidth,
      height: offsetHeight
    };
    return offset;
  }
  const self = {
    measure
  };
  return self;
}

function PercentOfView(viewSize) {
  function measure(n) {
    return viewSize * (n / 100);
  }
  const self = {
    measure
  };
  return self;
}

function ResizeHandler(container, eventHandler, ownerWindow, slides, axis, watchResize, nodeRects) {
  const observeNodes = [container].concat(slides);
  let resizeObserver;
  let containerSize;
  let slideSizes = [];
  let destroyed = false;
  function readSize(node) {
    return axis.measureSize(nodeRects.measure(node));
  }
  function init(emblaApi) {
    if (!watchResize) return;
    containerSize = readSize(container);
    slideSizes = slides.map(readSize);
    function defaultCallback(entries) {
      for (const entry of entries) {
        if (destroyed) return;
        const isContainer = entry.target === container;
        const slideIndex = slides.indexOf(entry.target);
        const lastSize = isContainer ? containerSize : slideSizes[slideIndex];
        const newSize = readSize(isContainer ? container : slides[slideIndex]);
        const diffSize = mathAbs(newSize - lastSize);
        if (diffSize >= 0.5) {
          emblaApi.reInit();
          eventHandler.emit('resize');
          break;
        }
      }
    }
    resizeObserver = new ResizeObserver(entries => {
      if (isBoolean(watchResize) || watchResize(emblaApi, entries)) {
        defaultCallback(entries);
      }
    });
    ownerWindow.requestAnimationFrame(() => {
      observeNodes.forEach(node => resizeObserver.observe(node));
    });
  }
  function destroy() {
    destroyed = true;
    if (resizeObserver) resizeObserver.disconnect();
  }
  const self = {
    init,
    destroy
  };
  return self;
}

function ScrollBody(location, offsetLocation, previousLocation, target, baseDuration, baseFriction) {
  let scrollVelocity = 0;
  let scrollDirection = 0;
  let scrollDuration = baseDuration;
  let scrollFriction = baseFriction;
  let rawLocation = location.get();
  let rawLocationPrevious = 0;
  function seek() {
    const displacement = target.get() - location.get();
    const isInstant = !scrollDuration;
    let scrollDistance = 0;
    if (isInstant) {
      scrollVelocity = 0;
      previousLocation.set(target);
      location.set(target);
      scrollDistance = displacement;
    } else {
      previousLocation.set(location);
      scrollVelocity += displacement / scrollDuration;
      scrollVelocity *= scrollFriction;
      rawLocation += scrollVelocity;
      location.add(scrollVelocity);
      scrollDistance = rawLocation - rawLocationPrevious;
    }
    scrollDirection = mathSign(scrollDistance);
    rawLocationPrevious = rawLocation;
    return self;
  }
  function settled() {
    const diff = target.get() - offsetLocation.get();
    return mathAbs(diff) < 0.001;
  }
  function duration() {
    return scrollDuration;
  }
  function direction() {
    return scrollDirection;
  }
  function velocity() {
    return scrollVelocity;
  }
  function useBaseDuration() {
    return useDuration(baseDuration);
  }
  function useBaseFriction() {
    return useFriction(baseFriction);
  }
  function useDuration(n) {
    scrollDuration = n;
    return self;
  }
  function useFriction(n) {
    scrollFriction = n;
    return self;
  }
  const self = {
    direction,
    duration,
    velocity,
    seek,
    settled,
    useBaseFriction,
    useBaseDuration,
    useFriction,
    useDuration
  };
  return self;
}

function ScrollBounds(limit, location, target, scrollBody, percentOfView) {
  const pullBackThreshold = percentOfView.measure(10);
  const edgeOffsetTolerance = percentOfView.measure(50);
  const frictionLimit = Limit(0.1, 0.99);
  let disabled = false;
  function shouldConstrain() {
    if (disabled) return false;
    if (!limit.reachedAny(target.get())) return false;
    if (!limit.reachedAny(location.get())) return false;
    return true;
  }
  function constrain(pointerDown) {
    if (!shouldConstrain()) return;
    const edge = limit.reachedMin(location.get()) ? 'min' : 'max';
    const diffToEdge = mathAbs(limit[edge] - location.get());
    const diffToTarget = target.get() - location.get();
    const friction = frictionLimit.constrain(diffToEdge / edgeOffsetTolerance);
    target.subtract(diffToTarget * friction);
    if (!pointerDown && mathAbs(diffToTarget) < pullBackThreshold) {
      target.set(limit.constrain(target.get()));
      scrollBody.useDuration(25).useBaseFriction();
    }
  }
  function toggleActive(active) {
    disabled = !active;
  }
  const self = {
    shouldConstrain,
    constrain,
    toggleActive
  };
  return self;
}

function ScrollContain(viewSize, contentSize, snapsAligned, containScroll, pixelTolerance) {
  const scrollBounds = Limit(-contentSize + viewSize, 0);
  const snapsBounded = measureBounded();
  const scrollContainLimit = findScrollContainLimit();
  const snapsContained = measureContained();
  function usePixelTolerance(bound, snap) {
    return deltaAbs(bound, snap) <= 1;
  }
  function findScrollContainLimit() {
    const startSnap = snapsBounded[0];
    const endSnap = arrayLast(snapsBounded);
    const min = snapsBounded.lastIndexOf(startSnap);
    const max = snapsBounded.indexOf(endSnap) + 1;
    return Limit(min, max);
  }
  function measureBounded() {
    return snapsAligned.map((snapAligned, index) => {
      const {
        min,
        max
      } = scrollBounds;
      const snap = scrollBounds.constrain(snapAligned);
      const isFirst = !index;
      const isLast = arrayIsLastIndex(snapsAligned, index);
      if (isFirst) return max;
      if (isLast) return min;
      if (usePixelTolerance(min, snap)) return min;
      if (usePixelTolerance(max, snap)) return max;
      return snap;
    }).map(scrollBound => parseFloat(scrollBound.toFixed(3)));
  }
  function measureContained() {
    if (contentSize <= viewSize + pixelTolerance) return [scrollBounds.max];
    if (containScroll === 'keepSnaps') return snapsBounded;
    const {
      min,
      max
    } = scrollContainLimit;
    return snapsBounded.slice(min, max);
  }
  const self = {
    snapsContained,
    scrollContainLimit
  };
  return self;
}

function ScrollLimit(contentSize, scrollSnaps, loop) {
  const max = scrollSnaps[0];
  const min = loop ? max - contentSize : arrayLast(scrollSnaps);
  const limit = Limit(min, max);
  const self = {
    limit
  };
  return self;
}

function ScrollLooper(contentSize, limit, location, vectors) {
  const jointSafety = 0.1;
  const min = limit.min + jointSafety;
  const max = limit.max + jointSafety;
  const {
    reachedMin,
    reachedMax
  } = Limit(min, max);
  function shouldLoop(direction) {
    if (direction === 1) return reachedMax(location.get());
    if (direction === -1) return reachedMin(location.get());
    return false;
  }
  function loop(direction) {
    if (!shouldLoop(direction)) return;
    const loopDistance = contentSize * (direction * -1);
    vectors.forEach(v => v.add(loopDistance));
  }
  const self = {
    loop
  };
  return self;
}

function ScrollProgress(limit) {
  const {
    max,
    length
  } = limit;
  function get(n) {
    const currentLocation = n - max;
    return length ? currentLocation / -length : 0;
  }
  const self = {
    get
  };
  return self;
}

function ScrollSnaps(axis, alignment, containerRect, slideRects, slidesToScroll) {
  const {
    startEdge,
    endEdge
  } = axis;
  const {
    groupSlides
  } = slidesToScroll;
  const alignments = measureSizes().map(alignment.measure);
  const snaps = measureUnaligned();
  const snapsAligned = measureAligned();
  function measureSizes() {
    return groupSlides(slideRects).map(rects => arrayLast(rects)[endEdge] - rects[0][startEdge]).map(mathAbs);
  }
  function measureUnaligned() {
    return slideRects.map(rect => containerRect[startEdge] - rect[startEdge]).map(snap => -mathAbs(snap));
  }
  function measureAligned() {
    return groupSlides(snaps).map(g => g[0]).map((snap, index) => snap + alignments[index]);
  }
  const self = {
    snaps,
    snapsAligned
  };
  return self;
}

function SlideRegistry(containSnaps, containScroll, scrollSnaps, scrollContainLimit, slidesToScroll, slideIndexes) {
  const {
    groupSlides
  } = slidesToScroll;
  const {
    min,
    max
  } = scrollContainLimit;
  const slideRegistry = createSlideRegistry();
  function createSlideRegistry() {
    const groupedSlideIndexes = groupSlides(slideIndexes);
    const doNotContain = !containSnaps || containScroll === 'keepSnaps';
    if (scrollSnaps.length === 1) return [slideIndexes];
    if (doNotContain) return groupedSlideIndexes;
    return groupedSlideIndexes.slice(min, max).map((group, index, groups) => {
      const isFirst = !index;
      const isLast = arrayIsLastIndex(groups, index);
      if (isFirst) {
        const range = arrayLast(groups[0]) + 1;
        return arrayFromNumber(range);
      }
      if (isLast) {
        const range = arrayLastIndex(slideIndexes) - arrayLast(groups)[0] + 1;
        return arrayFromNumber(range, arrayLast(groups)[0]);
      }
      return group;
    });
  }
  const self = {
    slideRegistry
  };
  return self;
}

function ScrollTarget(loop, scrollSnaps, contentSize, limit, targetVector) {
  const {
    reachedAny,
    removeOffset,
    constrain
  } = limit;
  function minDistance(distances) {
    return distances.concat().sort((a, b) => mathAbs(a) - mathAbs(b))[0];
  }
  function findTargetSnap(target) {
    const distance = loop ? removeOffset(target) : constrain(target);
    const ascDiffsToSnaps = scrollSnaps.map((snap, index) => ({
      diff: shortcut(snap - distance, 0),
      index
    })).sort((d1, d2) => mathAbs(d1.diff) - mathAbs(d2.diff));
    const {
      index
    } = ascDiffsToSnaps[0];
    return {
      index,
      distance
    };
  }
  function shortcut(target, direction) {
    const targets = [target, target + contentSize, target - contentSize];
    if (!loop) return target;
    if (!direction) return minDistance(targets);
    const matchingTargets = targets.filter(t => mathSign(t) === direction);
    if (matchingTargets.length) return minDistance(matchingTargets);
    return arrayLast(targets) - contentSize;
  }
  function byIndex(index, direction) {
    const diffToSnap = scrollSnaps[index] - targetVector.get();
    const distance = shortcut(diffToSnap, direction);
    return {
      index,
      distance
    };
  }
  function byDistance(distance, snap) {
    const target = targetVector.get() + distance;
    const {
      index,
      distance: targetSnapDistance
    } = findTargetSnap(target);
    const reachedBound = !loop && reachedAny(target);
    if (!snap || reachedBound) return {
      index,
      distance
    };
    const diffToSnap = scrollSnaps[index] - targetSnapDistance;
    const snapDistance = distance + shortcut(diffToSnap, 0);
    return {
      index,
      distance: snapDistance
    };
  }
  const self = {
    byDistance,
    byIndex,
    shortcut
  };
  return self;
}

function ScrollTo(animation, indexCurrent, indexPrevious, scrollBody, scrollTarget, targetVector, eventHandler) {
  function scrollTo(target) {
    const distanceDiff = target.distance;
    const indexDiff = target.index !== indexCurrent.get();
    targetVector.add(distanceDiff);
    if (distanceDiff) {
      if (scrollBody.duration()) {
        animation.start();
      } else {
        animation.update();
        animation.render(1);
        animation.update();
      }
    }
    if (indexDiff) {
      indexPrevious.set(indexCurrent.get());
      indexCurrent.set(target.index);
      eventHandler.emit('select');
    }
  }
  function distance(n, snap) {
    const target = scrollTarget.byDistance(n, snap);
    scrollTo(target);
  }
  function index(n, direction) {
    const targetIndex = indexCurrent.clone().set(n);
    const target = scrollTarget.byIndex(targetIndex.get(), direction);
    scrollTo(target);
  }
  const self = {
    distance,
    index
  };
  return self;
}

function SlideFocus(root, slides, slideRegistry, scrollTo, scrollBody, eventStore, eventHandler, watchFocus) {
  const focusListenerOptions = {
    passive: true,
    capture: true
  };
  let lastTabPressTime = 0;
  function init(emblaApi) {
    if (!watchFocus) return;
    function defaultCallback(index) {
      const nowTime = new Date().getTime();
      const diffTime = nowTime - lastTabPressTime;
      if (diffTime > 10) return;
      eventHandler.emit('slideFocusStart');
      root.scrollLeft = 0;
      const group = slideRegistry.findIndex(group => group.includes(index));
      if (!isNumber(group)) return;
      scrollBody.useDuration(0);
      scrollTo.index(group, 0);
      eventHandler.emit('slideFocus');
    }
    eventStore.add(document, 'keydown', registerTabPress, false);
    slides.forEach((slide, slideIndex) => {
      eventStore.add(slide, 'focus', evt => {
        if (isBoolean(watchFocus) || watchFocus(emblaApi, evt)) {
          defaultCallback(slideIndex);
        }
      }, focusListenerOptions);
    });
  }
  function registerTabPress(event) {
    if (event.code === 'Tab') lastTabPressTime = new Date().getTime();
  }
  const self = {
    init
  };
  return self;
}

function Vector1D(initialValue) {
  let value = initialValue;
  function get() {
    return value;
  }
  function set(n) {
    value = normalizeInput(n);
  }
  function add(n) {
    value += normalizeInput(n);
  }
  function subtract(n) {
    value -= normalizeInput(n);
  }
  function normalizeInput(n) {
    return isNumber(n) ? n : n.get();
  }
  const self = {
    get,
    set,
    add,
    subtract
  };
  return self;
}

function Translate(axis, container) {
  const translate = axis.scroll === 'x' ? x : y;
  const containerStyle = container.style;
  let previousTarget = null;
  let disabled = false;
  function x(n) {
    return `translate3d(${n}px,0px,0px)`;
  }
  function y(n) {
    return `translate3d(0px,${n}px,0px)`;
  }
  function to(target) {
    if (disabled) return;
    const newTarget = roundToTwoDecimals(axis.direction(target));
    if (newTarget === previousTarget) return;
    containerStyle.transform = translate(newTarget);
    previousTarget = newTarget;
  }
  function toggleActive(active) {
    disabled = !active;
  }
  function clear() {
    if (disabled) return;
    containerStyle.transform = '';
    if (!container.getAttribute('style')) container.removeAttribute('style');
  }
  const self = {
    clear,
    to,
    toggleActive
  };
  return self;
}

function SlideLooper(axis, viewSize, contentSize, slideSizes, slideSizesWithGaps, snaps, scrollSnaps, location, slides) {
  const roundingSafety = 0.5;
  const ascItems = arrayKeys(slideSizesWithGaps);
  const descItems = arrayKeys(slideSizesWithGaps).reverse();
  const loopPoints = startPoints().concat(endPoints());
  function removeSlideSizes(indexes, from) {
    return indexes.reduce((a, i) => {
      return a - slideSizesWithGaps[i];
    }, from);
  }
  function slidesInGap(indexes, gap) {
    return indexes.reduce((a, i) => {
      const remainingGap = removeSlideSizes(a, gap);
      return remainingGap > 0 ? a.concat([i]) : a;
    }, []);
  }
  function findSlideBounds(offset) {
    return snaps.map((snap, index) => ({
      start: snap - slideSizes[index] + roundingSafety + offset,
      end: snap + viewSize - roundingSafety + offset
    }));
  }
  function findLoopPoints(indexes, offset, isEndEdge) {
    const slideBounds = findSlideBounds(offset);
    return indexes.map(index => {
      const initial = isEndEdge ? 0 : -contentSize;
      const altered = isEndEdge ? contentSize : 0;
      const boundEdge = isEndEdge ? 'end' : 'start';
      const loopPoint = slideBounds[index][boundEdge];
      return {
        index,
        loopPoint,
        slideLocation: Vector1D(-1),
        translate: Translate(axis, slides[index]),
        target: () => location.get() > loopPoint ? initial : altered
      };
    });
  }
  function startPoints() {
    const gap = scrollSnaps[0];
    const indexes = slidesInGap(descItems, gap);
    return findLoopPoints(indexes, contentSize, false);
  }
  function endPoints() {
    const gap = viewSize - scrollSnaps[0] - 1;
    const indexes = slidesInGap(ascItems, gap);
    return findLoopPoints(indexes, -contentSize, true);
  }
  function canLoop() {
    return loopPoints.every(({
      index
    }) => {
      const otherIndexes = ascItems.filter(i => i !== index);
      return removeSlideSizes(otherIndexes, viewSize) <= 0.1;
    });
  }
  function loop() {
    loopPoints.forEach(loopPoint => {
      const {
        target,
        translate,
        slideLocation
      } = loopPoint;
      const shiftLocation = target();
      if (shiftLocation === slideLocation.get()) return;
      translate.to(shiftLocation);
      slideLocation.set(shiftLocation);
    });
  }
  function clear() {
    loopPoints.forEach(loopPoint => loopPoint.translate.clear());
  }
  const self = {
    canLoop,
    clear,
    loop,
    loopPoints
  };
  return self;
}

function SlidesHandler(container, eventHandler, watchSlides) {
  let mutationObserver;
  let destroyed = false;
  function init(emblaApi) {
    if (!watchSlides) return;
    function defaultCallback(mutations) {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          emblaApi.reInit();
          eventHandler.emit('slidesChanged');
          break;
        }
      }
    }
    mutationObserver = new MutationObserver(mutations => {
      if (destroyed) return;
      if (isBoolean(watchSlides) || watchSlides(emblaApi, mutations)) {
        defaultCallback(mutations);
      }
    });
    mutationObserver.observe(container, {
      childList: true
    });
  }
  function destroy() {
    if (mutationObserver) mutationObserver.disconnect();
    destroyed = true;
  }
  const self = {
    init,
    destroy
  };
  return self;
}

function SlidesInView(container, slides, eventHandler, threshold) {
  const intersectionEntryMap = {};
  let inViewCache = null;
  let notInViewCache = null;
  let intersectionObserver;
  let destroyed = false;
  function init() {
    intersectionObserver = new IntersectionObserver(entries => {
      if (destroyed) return;
      entries.forEach(entry => {
        const index = slides.indexOf(entry.target);
        intersectionEntryMap[index] = entry;
      });
      inViewCache = null;
      notInViewCache = null;
      eventHandler.emit('slidesInView');
    }, {
      root: container.parentElement,
      threshold
    });
    slides.forEach(slide => intersectionObserver.observe(slide));
  }
  function destroy() {
    if (intersectionObserver) intersectionObserver.disconnect();
    destroyed = true;
  }
  function createInViewList(inView) {
    return objectKeys(intersectionEntryMap).reduce((list, slideIndex) => {
      const index = parseInt(slideIndex);
      const {
        isIntersecting
      } = intersectionEntryMap[index];
      const inViewMatch = inView && isIntersecting;
      const notInViewMatch = !inView && !isIntersecting;
      if (inViewMatch || notInViewMatch) list.push(index);
      return list;
    }, []);
  }
  function get(inView = true) {
    if (inView && inViewCache) return inViewCache;
    if (!inView && notInViewCache) return notInViewCache;
    const slideIndexes = createInViewList(inView);
    if (inView) inViewCache = slideIndexes;
    if (!inView) notInViewCache = slideIndexes;
    return slideIndexes;
  }
  const self = {
    init,
    destroy,
    get
  };
  return self;
}

function SlideSizes(axis, containerRect, slideRects, slides, readEdgeGap, ownerWindow) {
  const {
    measureSize,
    startEdge,
    endEdge
  } = axis;
  const withEdgeGap = slideRects[0] && readEdgeGap;
  const startGap = measureStartGap();
  const endGap = measureEndGap();
  const slideSizes = slideRects.map(measureSize);
  const slideSizesWithGaps = measureWithGaps();
  function measureStartGap() {
    if (!withEdgeGap) return 0;
    const slideRect = slideRects[0];
    return mathAbs(containerRect[startEdge] - slideRect[startEdge]);
  }
  function measureEndGap() {
    if (!withEdgeGap) return 0;
    const style = ownerWindow.getComputedStyle(arrayLast(slides));
    return parseFloat(style.getPropertyValue(`margin-${endEdge}`));
  }
  function measureWithGaps() {
    return slideRects.map((rect, index, rects) => {
      const isFirst = !index;
      const isLast = arrayIsLastIndex(rects, index);
      if (isFirst) return slideSizes[index] + startGap;
      if (isLast) return slideSizes[index] + endGap;
      return rects[index + 1][startEdge] - rect[startEdge];
    }).map(mathAbs);
  }
  const self = {
    slideSizes,
    slideSizesWithGaps,
    startGap,
    endGap
  };
  return self;
}

function SlidesToScroll(axis, viewSize, slidesToScroll, loop, containerRect, slideRects, startGap, endGap, pixelTolerance) {
  const {
    startEdge,
    endEdge,
    direction
  } = axis;
  const groupByNumber = isNumber(slidesToScroll);
  function byNumber(array, groupSize) {
    return arrayKeys(array).filter(i => i % groupSize === 0).map(i => array.slice(i, i + groupSize));
  }
  function bySize(array) {
    if (!array.length) return [];
    return arrayKeys(array).reduce((groups, rectB, index) => {
      const rectA = arrayLast(groups) || 0;
      const isFirst = rectA === 0;
      const isLast = rectB === arrayLastIndex(array);
      const edgeA = containerRect[startEdge] - slideRects[rectA][startEdge];
      const edgeB = containerRect[startEdge] - slideRects[rectB][endEdge];
      const gapA = !loop && isFirst ? direction(startGap) : 0;
      const gapB = !loop && isLast ? direction(endGap) : 0;
      const chunkSize = mathAbs(edgeB - gapB - (edgeA + gapA));
      if (index && chunkSize > viewSize + pixelTolerance) groups.push(rectB);
      if (isLast) groups.push(array.length);
      return groups;
    }, []).map((currentSize, index, groups) => {
      const previousSize = Math.max(groups[index - 1] || 0);
      return array.slice(previousSize, currentSize);
    });
  }
  function groupSlides(array) {
    return groupByNumber ? byNumber(array, slidesToScroll) : bySize(array);
  }
  const self = {
    groupSlides
  };
  return self;
}

function Engine(root, container, slides, ownerDocument, ownerWindow, options, eventHandler) {
  // Options
  const {
    align,
    axis: scrollAxis,
    direction,
    startIndex,
    loop,
    duration,
    dragFree,
    dragThreshold,
    inViewThreshold,
    slidesToScroll: groupSlides,
    skipSnaps,
    containScroll,
    watchResize,
    watchSlides,
    watchDrag,
    watchFocus
  } = options;
  // Measurements
  const pixelTolerance = 2;
  const nodeRects = NodeRects();
  const containerRect = nodeRects.measure(container);
  const slideRects = slides.map(nodeRects.measure);
  const axis = Axis(scrollAxis, direction);
  const viewSize = axis.measureSize(containerRect);
  const percentOfView = PercentOfView(viewSize);
  const alignment = Alignment(align, viewSize);
  const containSnaps = !loop && !!containScroll;
  const readEdgeGap = loop || !!containScroll;
  const {
    slideSizes,
    slideSizesWithGaps,
    startGap,
    endGap
  } = SlideSizes(axis, containerRect, slideRects, slides, readEdgeGap, ownerWindow);
  const slidesToScroll = SlidesToScroll(axis, viewSize, groupSlides, loop, containerRect, slideRects, startGap, endGap, pixelTolerance);
  const {
    snaps,
    snapsAligned
  } = ScrollSnaps(axis, alignment, containerRect, slideRects, slidesToScroll);
  const contentSize = -arrayLast(snaps) + arrayLast(slideSizesWithGaps);
  const {
    snapsContained,
    scrollContainLimit
  } = ScrollContain(viewSize, contentSize, snapsAligned, containScroll, pixelTolerance);
  const scrollSnaps = containSnaps ? snapsContained : snapsAligned;
  const {
    limit
  } = ScrollLimit(contentSize, scrollSnaps, loop);
  // Indexes
  const index = Counter(arrayLastIndex(scrollSnaps), startIndex, loop);
  const indexPrevious = index.clone();
  const slideIndexes = arrayKeys(slides);
  // Animation
  const update = ({
    dragHandler,
    scrollBody,
    scrollBounds,
    options: {
      loop
    }
  }) => {
    if (!loop) scrollBounds.constrain(dragHandler.pointerDown());
    scrollBody.seek();
  };
  const render = ({
    scrollBody,
    translate,
    location,
    offsetLocation,
    previousLocation,
    scrollLooper,
    slideLooper,
    dragHandler,
    animation,
    eventHandler,
    scrollBounds,
    options: {
      loop
    }
  }, alpha) => {
    const shouldSettle = scrollBody.settled();
    const withinBounds = !scrollBounds.shouldConstrain();
    const hasSettled = loop ? shouldSettle : shouldSettle && withinBounds;
    const hasSettledAndIdle = hasSettled && !dragHandler.pointerDown();
    if (hasSettledAndIdle) animation.stop();
    const interpolatedLocation = location.get() * alpha + previousLocation.get() * (1 - alpha);
    offsetLocation.set(interpolatedLocation);
    if (loop) {
      scrollLooper.loop(scrollBody.direction());
      slideLooper.loop();
    }
    translate.to(offsetLocation.get());
    if (hasSettledAndIdle) eventHandler.emit('settle');
    if (!hasSettled) eventHandler.emit('scroll');
  };
  const animation = Animations(ownerDocument, ownerWindow, () => update(engine), alpha => render(engine, alpha));
  // Shared
  const friction = 0.68;
  const startLocation = scrollSnaps[index.get()];
  const location = Vector1D(startLocation);
  const previousLocation = Vector1D(startLocation);
  const offsetLocation = Vector1D(startLocation);
  const target = Vector1D(startLocation);
  const scrollBody = ScrollBody(location, offsetLocation, previousLocation, target, duration, friction);
  const scrollTarget = ScrollTarget(loop, scrollSnaps, contentSize, limit, target);
  const scrollTo = ScrollTo(animation, index, indexPrevious, scrollBody, scrollTarget, target, eventHandler);
  const scrollProgress = ScrollProgress(limit);
  const eventStore = EventStore();
  const slidesInView = SlidesInView(container, slides, eventHandler, inViewThreshold);
  const {
    slideRegistry
  } = SlideRegistry(containSnaps, containScroll, scrollSnaps, scrollContainLimit, slidesToScroll, slideIndexes);
  const slideFocus = SlideFocus(root, slides, slideRegistry, scrollTo, scrollBody, eventStore, eventHandler, watchFocus);
  // Engine
  const engine = {
    ownerDocument,
    ownerWindow,
    eventHandler,
    containerRect,
    slideRects,
    animation,
    axis,
    dragHandler: DragHandler(axis, root, ownerDocument, ownerWindow, target, DragTracker(axis, ownerWindow), location, animation, scrollTo, scrollBody, scrollTarget, index, eventHandler, percentOfView, dragFree, dragThreshold, skipSnaps, friction, watchDrag),
    eventStore,
    percentOfView,
    index,
    indexPrevious,
    limit,
    location,
    offsetLocation,
    previousLocation,
    options,
    resizeHandler: ResizeHandler(container, eventHandler, ownerWindow, slides, axis, watchResize, nodeRects),
    scrollBody,
    scrollBounds: ScrollBounds(limit, offsetLocation, target, scrollBody, percentOfView),
    scrollLooper: ScrollLooper(contentSize, limit, offsetLocation, [location, offsetLocation, previousLocation, target]),
    scrollProgress,
    scrollSnapList: scrollSnaps.map(scrollProgress.get),
    scrollSnaps,
    scrollTarget,
    scrollTo,
    slideLooper: SlideLooper(axis, viewSize, contentSize, slideSizes, slideSizesWithGaps, snaps, scrollSnaps, offsetLocation, slides),
    slideFocus,
    slidesHandler: SlidesHandler(container, eventHandler, watchSlides),
    slidesInView,
    slideIndexes,
    slideRegistry,
    slidesToScroll,
    target,
    translate: Translate(axis, container)
  };
  return engine;
}

function EventHandler() {
  let listeners = {};
  let api;
  function init(emblaApi) {
    api = emblaApi;
  }
  function getListeners(evt) {
    return listeners[evt] || [];
  }
  function emit(evt) {
    getListeners(evt).forEach(e => e(api, evt));
    return self;
  }
  function on(evt, cb) {
    listeners[evt] = getListeners(evt).concat([cb]);
    return self;
  }
  function off(evt, cb) {
    listeners[evt] = getListeners(evt).filter(e => e !== cb);
    return self;
  }
  function clear() {
    listeners = {};
  }
  const self = {
    init,
    emit,
    off,
    on,
    clear
  };
  return self;
}

const defaultOptions$1 = {
  align: 'center',
  axis: 'x',
  container: null,
  slides: null,
  containScroll: 'trimSnaps',
  direction: 'ltr',
  slidesToScroll: 1,
  inViewThreshold: 0,
  breakpoints: {},
  dragFree: false,
  dragThreshold: 10,
  loop: false,
  skipSnaps: false,
  duration: 25,
  startIndex: 0,
  active: true,
  watchDrag: true,
  watchResize: true,
  watchSlides: true,
  watchFocus: true
};

function OptionsHandler(ownerWindow) {
  function mergeOptions(optionsA, optionsB) {
    return objectsMergeDeep(optionsA, optionsB || {});
  }
  function optionsAtMedia(options) {
    const optionsAtMedia = options.breakpoints || {};
    const matchedMediaOptions = objectKeys(optionsAtMedia).filter(media => ownerWindow.matchMedia(media).matches).map(media => optionsAtMedia[media]).reduce((a, mediaOption) => mergeOptions(a, mediaOption), {});
    return mergeOptions(options, matchedMediaOptions);
  }
  function optionsMediaQueries(optionsList) {
    return optionsList.map(options => objectKeys(options.breakpoints || {})).reduce((acc, mediaQueries) => acc.concat(mediaQueries), []).map(ownerWindow.matchMedia);
  }
  const self = {
    mergeOptions,
    optionsAtMedia,
    optionsMediaQueries
  };
  return self;
}

function PluginsHandler(optionsHandler) {
  let activePlugins = [];
  function init(emblaApi, plugins) {
    activePlugins = plugins.filter(({
      options
    }) => optionsHandler.optionsAtMedia(options).active !== false);
    activePlugins.forEach(plugin => plugin.init(emblaApi, optionsHandler));
    return plugins.reduce((map, plugin) => Object.assign(map, {
      [plugin.name]: plugin
    }), {});
  }
  function destroy() {
    activePlugins = activePlugins.filter(plugin => plugin.destroy());
  }
  const self = {
    init,
    destroy
  };
  return self;
}

function EmblaCarousel(root, userOptions, userPlugins) {
  const ownerDocument = root.ownerDocument;
  const ownerWindow = ownerDocument.defaultView;
  const optionsHandler = OptionsHandler(ownerWindow);
  const pluginsHandler = PluginsHandler(optionsHandler);
  const mediaHandlers = EventStore();
  const eventHandler = EventHandler();
  const {
    mergeOptions,
    optionsAtMedia,
    optionsMediaQueries
  } = optionsHandler;
  const {
    on,
    off,
    emit
  } = eventHandler;
  const reInit = reActivate;
  let destroyed = false;
  let engine;
  let optionsBase = mergeOptions(defaultOptions$1, EmblaCarousel.globalOptions);
  let options = mergeOptions(optionsBase);
  let pluginList = [];
  let pluginApis;
  let container;
  let slides;
  function storeElements() {
    const {
      container: userContainer,
      slides: userSlides
    } = options;
    const customContainer = isString(userContainer) ? root.querySelector(userContainer) : userContainer;
    container = customContainer || root.children[0];
    const customSlides = isString(userSlides) ? container.querySelectorAll(userSlides) : userSlides;
    slides = [].slice.call(customSlides || container.children);
  }
  function createEngine(options) {
    const engine = Engine(root, container, slides, ownerDocument, ownerWindow, options, eventHandler);
    if (options.loop && !engine.slideLooper.canLoop()) {
      const optionsWithoutLoop = Object.assign({}, options, {
        loop: false
      });
      return createEngine(optionsWithoutLoop);
    }
    return engine;
  }
  function activate(withOptions, withPlugins) {
    if (destroyed) return;
    optionsBase = mergeOptions(optionsBase, withOptions);
    options = optionsAtMedia(optionsBase);
    pluginList = withPlugins || pluginList;
    storeElements();
    engine = createEngine(options);
    optionsMediaQueries([optionsBase, ...pluginList.map(({
      options
    }) => options)]).forEach(query => mediaHandlers.add(query, 'change', reActivate));
    if (!options.active) return;
    engine.translate.to(engine.location.get());
    engine.animation.init();
    engine.slidesInView.init();
    engine.slideFocus.init(self);
    engine.eventHandler.init(self);
    engine.resizeHandler.init(self);
    engine.slidesHandler.init(self);
    if (engine.options.loop) engine.slideLooper.loop();
    if (container.offsetParent && slides.length) engine.dragHandler.init(self);
    pluginApis = pluginsHandler.init(self, pluginList);
  }
  function reActivate(withOptions, withPlugins) {
    const startIndex = selectedScrollSnap();
    deActivate();
    activate(mergeOptions({
      startIndex
    }, withOptions), withPlugins);
    eventHandler.emit('reInit');
  }
  function deActivate() {
    engine.dragHandler.destroy();
    engine.eventStore.clear();
    engine.translate.clear();
    engine.slideLooper.clear();
    engine.resizeHandler.destroy();
    engine.slidesHandler.destroy();
    engine.slidesInView.destroy();
    engine.animation.destroy();
    pluginsHandler.destroy();
    mediaHandlers.clear();
  }
  function destroy() {
    if (destroyed) return;
    destroyed = true;
    mediaHandlers.clear();
    deActivate();
    eventHandler.emit('destroy');
    eventHandler.clear();
  }
  function scrollTo(index, jump, direction) {
    if (!options.active || destroyed) return;
    engine.scrollBody.useBaseFriction().useDuration(jump === true ? 0 : options.duration);
    engine.scrollTo.index(index, direction || 0);
  }
  function scrollNext(jump) {
    const next = engine.index.add(1).get();
    scrollTo(next, jump, -1);
  }
  function scrollPrev(jump) {
    const prev = engine.index.add(-1).get();
    scrollTo(prev, jump, 1);
  }
  function canScrollNext() {
    const next = engine.index.add(1).get();
    return next !== selectedScrollSnap();
  }
  function canScrollPrev() {
    const prev = engine.index.add(-1).get();
    return prev !== selectedScrollSnap();
  }
  function scrollSnapList() {
    return engine.scrollSnapList;
  }
  function scrollProgress() {
    return engine.scrollProgress.get(engine.offsetLocation.get());
  }
  function selectedScrollSnap() {
    return engine.index.get();
  }
  function previousScrollSnap() {
    return engine.indexPrevious.get();
  }
  function slidesInView() {
    return engine.slidesInView.get();
  }
  function slidesNotInView() {
    return engine.slidesInView.get(false);
  }
  function plugins() {
    return pluginApis;
  }
  function internalEngine() {
    return engine;
  }
  function rootNode() {
    return root;
  }
  function containerNode() {
    return container;
  }
  function slideNodes() {
    return slides;
  }
  const self = {
    canScrollNext,
    canScrollPrev,
    containerNode,
    internalEngine,
    destroy,
    off,
    on,
    emit,
    plugins,
    previousScrollSnap,
    reInit,
    rootNode,
    scrollNext,
    scrollPrev,
    scrollProgress,
    scrollSnapList,
    scrollTo,
    selectedScrollSnap,
    slideNodes,
    slidesInView,
    slidesNotInView
  };
  activate(userOptions, userPlugins);
  setTimeout(() => eventHandler.emit('init'), 0);
  return self;
}
EmblaCarousel.globalOptions = undefined;

const defaultOptions = {
  active: true,
  breakpoints: {},
  delay: 4000,
  jump: false,
  playOnInit: true,
  stopOnFocusIn: true,
  stopOnInteraction: true,
  stopOnMouseEnter: false,
  stopOnLastSnap: false,
  rootNode: null
};

function normalizeDelay(emblaApi, delay) {
  const scrollSnaps = emblaApi.scrollSnapList();
  if (typeof delay === 'number') {
    return scrollSnaps.map(() => delay);
  }
  return delay(scrollSnaps, emblaApi);
}
function getAutoplayRootNode(emblaApi, rootNode) {
  const emblaRootNode = emblaApi.rootNode();
  return rootNode && rootNode(emblaRootNode) || emblaRootNode;
}

function Autoplay(userOptions = {}) {
  let options;
  let emblaApi;
  let destroyed;
  let delay;
  let timerStartTime = null;
  let timerId = 0;
  let autoplayActive = false;
  let mouseIsOver = false;
  let playOnDocumentVisible = false;
  let jump = false;
  function init(emblaApiInstance, optionsHandler) {
    emblaApi = emblaApiInstance;
    const {
      mergeOptions,
      optionsAtMedia
    } = optionsHandler;
    const optionsBase = mergeOptions(defaultOptions, Autoplay.globalOptions);
    const allOptions = mergeOptions(optionsBase, userOptions);
    options = optionsAtMedia(allOptions);
    if (emblaApi.scrollSnapList().length <= 1) return;
    jump = options.jump;
    destroyed = false;
    delay = normalizeDelay(emblaApi, options.delay);
    const {
      eventStore,
      ownerDocument
    } = emblaApi.internalEngine();
    const isDraggable = !!emblaApi.internalEngine().options.watchDrag;
    const root = getAutoplayRootNode(emblaApi, options.rootNode);
    eventStore.add(ownerDocument, 'visibilitychange', visibilityChange);
    if (isDraggable) {
      emblaApi.on('pointerDown', pointerDown);
    }
    if (isDraggable && !options.stopOnInteraction) {
      emblaApi.on('pointerUp', pointerUp);
    }
    if (options.stopOnMouseEnter) {
      eventStore.add(root, 'mouseenter', mouseEnter);
    }
    if (options.stopOnMouseEnter && !options.stopOnInteraction) {
      eventStore.add(root, 'mouseleave', mouseLeave);
    }
    if (options.stopOnFocusIn) {
      emblaApi.on('slideFocusStart', stopAutoplay);
    }
    if (options.stopOnFocusIn && !options.stopOnInteraction) {
      eventStore.add(emblaApi.containerNode(), 'focusout', startAutoplay);
    }
    if (options.playOnInit) startAutoplay();
  }
  function destroy() {
    emblaApi.off('pointerDown', pointerDown).off('pointerUp', pointerUp).off('slideFocusStart', stopAutoplay);
    stopAutoplay();
    destroyed = true;
    autoplayActive = false;
  }
  function setTimer() {
    const {
      ownerWindow
    } = emblaApi.internalEngine();
    ownerWindow.clearTimeout(timerId);
    timerId = ownerWindow.setTimeout(next, delay[emblaApi.selectedScrollSnap()]);
    timerStartTime = new Date().getTime();
    emblaApi.emit('autoplay:timerset');
  }
  function clearTimer() {
    const {
      ownerWindow
    } = emblaApi.internalEngine();
    ownerWindow.clearTimeout(timerId);
    timerId = 0;
    timerStartTime = null;
    emblaApi.emit('autoplay:timerstopped');
  }
  function startAutoplay() {
    if (destroyed) return;
    if (documentIsHidden()) {
      playOnDocumentVisible = true;
      return;
    }
    if (!autoplayActive) emblaApi.emit('autoplay:play');
    setTimer();
    autoplayActive = true;
  }
  function stopAutoplay() {
    if (destroyed) return;
    if (autoplayActive) emblaApi.emit('autoplay:stop');
    clearTimer();
    autoplayActive = false;
  }
  function visibilityChange() {
    if (documentIsHidden()) {
      playOnDocumentVisible = autoplayActive;
      return stopAutoplay();
    }
    if (playOnDocumentVisible) startAutoplay();
  }
  function documentIsHidden() {
    const {
      ownerDocument
    } = emblaApi.internalEngine();
    return ownerDocument.visibilityState === 'hidden';
  }
  function pointerDown() {
    if (!mouseIsOver) stopAutoplay();
  }
  function pointerUp() {
    if (!mouseIsOver) startAutoplay();
  }
  function mouseEnter() {
    mouseIsOver = true;
    stopAutoplay();
  }
  function mouseLeave() {
    mouseIsOver = false;
    startAutoplay();
  }
  function play(jumpOverride) {
    if (typeof jumpOverride !== 'undefined') jump = jumpOverride;
    startAutoplay();
  }
  function stop() {
    if (autoplayActive) stopAutoplay();
  }
  function reset() {
    if (autoplayActive) startAutoplay();
  }
  function isPlaying() {
    return autoplayActive;
  }
  function next() {
    const {
      index
    } = emblaApi.internalEngine();
    const nextIndex = index.clone().add(1).get();
    const lastIndex = emblaApi.scrollSnapList().length - 1;
    const kill = options.stopOnLastSnap && nextIndex === lastIndex;
    if (emblaApi.canScrollNext()) {
      emblaApi.scrollNext(jump);
    } else {
      emblaApi.scrollTo(0, jump);
    }
    emblaApi.emit('autoplay:select');
    if (kill) return stopAutoplay();
    startAutoplay();
  }
  function timeUntilNext() {
    if (!timerStartTime) return null;
    const currentDelay = delay[emblaApi.selectedScrollSnap()];
    const timePastSinceStart = new Date().getTime() - timerStartTime;
    return currentDelay - timePastSinceStart;
  }
  const self = {
    name: 'autoplay',
    options: userOptions,
    init,
    destroy,
    play,
    stop,
    reset,
    isPlaying,
    timeUntilNext
  };
  return self;
}
Autoplay.globalOptions = undefined;

class CarouselSlider extends HTMLElement {
  constructor() {
    super();

    this.axis = this.getAttribute('axis') || 'x';
    this.progress = this.getAttribute('progress') == 'true';
    this.isThumbnailCarousel = this.getAttribute('data-thumb-carousel') == 'true';
    this.isMainCarousel = this.getAttribute('data-main-carousel') == 'true';
    this.mainCarousel = document.querySelector('[data-main-carousel="true"]');
    this.isVideoCarousel = this.getAttribute('data-video-carousel') == 'true';

    this.wrapperNode = this.querySelector('.embla');
    this.viewportNode = this.querySelector('.embla__viewport');
    this.prevButtonNode = this.querySelector('.embla__prev');
    this.nextButtonNode = this.querySelector('.embla__next');
    this.progressNode = this.querySelector('.embla__progress__bar');

    this.startIndex = Number(this.getAttribute('start-index')) || 0;
    this.align = this.getAttribute('align') || 'start';
    this.autoPlay = this.getAttribute('autoplay') == 'true';

    this.thumbnailButtons = this.querySelectorAll('.embla__slide__number');
    this.dotsWrapper = this.querySelector('.embla__dots');
    this.dotNodes = [];

    // Breakpoint-only init
    // Change this if you want a different mobile cutoff.
    this.mobileOnly = this.getAttribute('mobile-only') == 'true';
    this.mobileMedia = window.matchMedia('(max-width: 767px)');
    this._onMobileChange = () => this.handleMobileInit();

    this.emblaApi = null;
    this._emblaListenersBound = false;

    // Store event handler references
    this.thumbnailHandlers = null;
    this.videoHandlers = null;
    this.dotHandlers = null;

    this.prevClickHandler = () => this.emblaApi?.scrollPrev?.();
    this.nextClickHandler = () => this.emblaApi?.scrollNext?.();

    this.toggleButtonsDisabledHandler = () => this.toggleButtonsDisabled();
    this.updateProgressHandler = () => this.updateProgress();
    this.toggleDotButtonsActiveHandler = () => this.toggleDotButtonsActive();
  }

  connectedCallback() {
    if (this.prevButtonNode) this.prevButtonNode.addEventListener('click', this.prevClickHandler, false);
    if (this.nextButtonNode) this.nextButtonNode.addEventListener('click', this.nextClickHandler, false);

    if (this.mobileOnly) {
      this.mobileMedia.addEventListener('change', this._onMobileChange);
      this.handleMobileInit();
    } else {
      this.initEmbla();
    }

    if (this.isThumbnailCarousel && this.mainCarousel) {
      this.thumbnailHandlers = new Map();

      for (const thumbnailButton of this.thumbnailButtons) {
        const handler = () => {
          const mainApi = this.mainCarousel.getEmblaApi?.();
          mainApi?.scrollTo?.(Number(thumbnailButton.getAttribute('data-index')), false, 'forward');
        };

        this.thumbnailHandlers.set(thumbnailButton, handler);
        thumbnailButton.addEventListener('click', handler);
      }
    }

    if (this.isVideoCarousel) {
      this.setupVideo();
    }

    // If it initializes immediately, toggle buttons/progress/dots after init.
  }

  disconnectedCallback() {
    if (this.prevButtonNode) this.prevButtonNode.removeEventListener('click', this.prevClickHandler);
    if (this.nextButtonNode) this.nextButtonNode.removeEventListener('click', this.nextClickHandler);

    this.mobileMedia.removeEventListener('change', this._onMobileChange);

    // Remove thumbnail listeners
    if (this.thumbnailHandlers) {
      for (const [button, handler] of this.thumbnailHandlers) {
        button.removeEventListener('click', handler);
      }
    }

    // Remove video listeners
    if (this.videoHandlers) {
      for (const [video, handlers] of this.videoHandlers) {
        video.removeEventListener('click', handlers.click);
        video.removeEventListener('play', handlers.play);
      }
    }

    // Remove dot button listeners
    if (this.dotNodes) {
      if (this.dotHandlers) {
        for (const [dotNode, handler] of this.dotHandlers) {
          dotNode.removeEventListener('click', handler);
        }
      }
    }

    // Destroy Embla if it exists
    this.destroyEmbla();

    // Clear dot nodes DOM (optional; safe)
    // If you want dots to remain on desktop markup, remove this.
    // this.dotsWrapper && (this.dotsWrapper.innerHTML = '');
  }

  handleMobileInit() {
    const shouldInit = this.mobileMedia.matches;

    if (shouldInit && !this.emblaApi) {
      this.initEmbla();
    } else if (!shouldInit && this.emblaApi) {
      this.destroyEmbla();
    }
  }

  initEmbla() {
    if (!this.viewportNode) return;

    const plugins = [];
    if (this.autoPlay) {
      plugins.push(Autoplay({ delay: 5000, playOnInit: true, stopOnInteraction: true }));
    }

    this.emblaApi = EmblaCarousel(
      this.viewportNode,
      {
        loop: false,
        axis: this.axis,
        align: this.align,
        startIndex: this.startIndex,
        duration: 50,
        slidesToScroll: 1,
        // draggable: false, // uncomment if you want non-draggable on mobile
      },
      plugins
    );

    // Embla listeners
    this.emblaApi.on('select', this.toggleButtonsDisabledHandler);
    this.emblaApi.on('reInit', this.toggleButtonsDisabledHandler);

    if (Boolean(this.progress) && this.progressNode) {
      this.emblaApi.on('reInit', this.updateProgressHandler);
      this.emblaApi.on('scroll', this.updateProgressHandler);
      this.emblaApi.on('slidefocus', this.updateProgressHandler);
    }

    if (this.dotsWrapper) {
      this.createAndSetupDotButtons();
      // Recreate dot buttons if Embla re-inits (e.g., responsive measurements)
      this.emblaApi.on('reInit', () => this.createAndSetupDotButtons());
      this.emblaApi.on('select', this.toggleDotButtonsActiveHandler);
    }

    // Make sure layout measurements are correct after images/fonts settle
    requestAnimationFrame(() => this.emblaApi?.reInit?.());
    window.addEventListener('load', () => this.emblaApi?.reInit?.(), { once: true });

    // Initial UI state
    this.toggleButtonsDisabled();
    this.updateProgress();
    this.toggleDotButtonsActive();
  }

  destroyEmbla() {
    if (!this.emblaApi) return;

    // Embla detach: easiest reliable cleanup is destroy()
    try {
      this.emblaApi.destroy();
    } catch (e) {
      // ignore
    }

    this.emblaApi = null;
  }

  getEmblaApi() {
    return this.emblaApi;
  }

  updateProgress() {
    if (!this.emblaApi || !this.progressNode) return;
    const progress = Math.max(0, Math.min(1, this.emblaApi.scrollProgress()));
    this.progressNode.style.transform = `translate3d(${progress * 100}%,0px,0px)`;
  }

  toggleButtonsDisabled() {
    if (!this.emblaApi) return;

    const setButtonState = (button, enabled) => {
      if (!button) return;
      button.toggleAttribute('disabled', !enabled);
    };

    setButtonState(this.prevButtonNode, this.emblaApi.canScrollPrev());
    setButtonState(this.nextButtonNode, this.emblaApi.canScrollNext());
  }

  setupVideo() {
    const videos = this.querySelectorAll('video');
    this.videoHandlers = new Map();

    for (const video of videos) {
      const clickHandler = () => {
        this.showControls({ video, videos });
      };
      const playHandler = () => {
        this.handleVideo({ video, videos });
      };

      this.videoHandlers.set(video, { click: clickHandler, play: playHandler });
      video.addEventListener('click', clickHandler);
      video.addEventListener('play', playHandler);
    }
  }

  showControls(options) {
    const { video, videos } = options;
    if (!video.controls) {
      video.controls = true;
      this.handleVideo({ video, videos });
    }
  }

  handleVideo(options) {
    const { video, videos } = options;

    for (const extvideo of videos) {
      if (video !== extvideo) extvideo.pause();
    }

    video.muted = false;
    video.play();
  }

  createDotButtonHtml() {
    const dotTemplate = this.querySelector('#embla-dot-template');
    if (!dotTemplate) return [];

    const snapList = this.emblaApi.scrollSnapList();

    if (this.dotsWrapper) {
      this.dotsWrapper.innerHTML = snapList.reduce((acc, _snap) => acc + dotTemplate.innerHTML, '');
    }

    return Array.from(this.dotsWrapper?.querySelectorAll('.embla__dot') ?? []);
  }

  addDotButtonClickHandlers() {
    this.dotHandlers = new Map();

    this.dotNodes.forEach((dotNode, index) => {
      const handler = () => {
        this.emblaApi?.scrollTo?.(index);
        // If autoplay plugin exists, reset it
        this.emblaApi?.plugins?.().autoplay?.reset?.();
      };

      this.dotHandlers.set(dotNode, handler);
      dotNode.addEventListener('click', handler, false);
    });
  }

  createAndSetupDotButtons() {
    if (!this.dotsWrapper || !this.emblaApi) return;

    if (this.dotNodes && this.dotNodes.length) {
      this.dotNodes.forEach((dot) => dot.remove());
    }
    this.dotNodes = this.createDotButtonHtml();
    this.addDotButtonClickHandlers();

    this.toggleDotButtonsActive();
  }

  toggleDotButtonsActive() {
    if (!this.emblaApi || !this.dotNodes || !this.dotNodes.length) return;

    const previous = this.emblaApi.previousScrollSnap();
    const selected = this.emblaApi.selectedScrollSnap();

    // previous can be -1 depending on state, so guard it
    if (previous >= 0 && this.dotNodes[previous]) {
      this.dotNodes[previous].classList.remove('embla__dot--selected');
    }

    if (this.dotNodes[selected]) {
      this.dotNodes[selected].classList.add('embla__dot--selected');
    }
  }
}

class FeaturedProducts extends HTMLElement {
	constructor() {
		super(); 
    this.carousel = this.querySelector('[data-ref="carousel"]');
    this.carouselSlider = this.carousel?.querySelector('carousel-slider');
    this.tabButtons = this.querySelectorAll('[data-ref="tabButtons"]');
    this.tabClickHandler = this.handleTabClick.bind(this);
	}

  connectedCallback() {
    for (const button of this.tabButtons) {
      button.addEventListener('click', this.tabClickHandler);
    }
  }

  disconnectedCallback() {
    for (const button of this.tabButtons) {
      button.removeEventListener('click', this.tabClickHandler);
    }
  }

  async handleTabClick(event) {
    const button = event.target.closest('[data-ref="tabButtons"]');
    if (!button) return;
    const collectionHandle = button.getAttribute('data-collection-handle');
    try {
      const response = await fetch(`/collections/${collectionHandle}/?sections=featured-products`);
      const data = await response.json();
      
      // Parse the returned section HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(data['featured-products'], 'text/html');
      const newCarousel = doc.querySelector('[data-ref="carousel"]');
      
      if (newCarousel) {
        // Before replacing innerHTML, manually clean up
        let emblaApi = this.carouselSlider.getEmblaApi();
        if (emblaApi) {
          emblaApi.destroy();  // Destroy before replacement
        }

        this.carousel.innerHTML = newCarousel.innerHTML;
        
        // Then reinitialize after new elements connect
        await customElements.whenDefined('carousel-slider');
        // Force reinitialization of newly connected elements

        
        // Update active button
        this.tabButtons.forEach(btn => {
          btn.classList.toggle('btn-primary', btn === button);
          btn.classList.toggle('btn-secondary', btn !== button);
        });

        // Reinitialize carousel
        emblaApi = this.carouselSlider.getEmblaApi();
        if (emblaApi) {
          emblaApi.reInit();
        }
      }
    } catch (error) {
      console.error('Error loading section:', error);
    }
  }
}

class SiteHeader extends HTMLElement {
  constructor() {
    super();
    this.searchButton = this.querySelectorAll('[data-ref="searchButton"]');
    this.predictiveSearch = document.querySelector('[data-ref="headerPredictiveSearch"]');
    this.searchCloseButton = this.predictiveSearch.querySelector('[data-ref="searchCloseButton"]');
    this.searchInput = document.querySelector('predictive-search [data-ref="searchInput"]');
    // Store handlers for proper cleanup
    this._onSearchClick = (event) => {
      event.preventDefault();
      this.openSearch();
    };

    this._onCloseClick = (event) => {
      event.preventDefault();
      this.closeSearch();
    };

    this._onDocClick = (event) => {
      if (!event.target.closest('[data-ref="headerPredictiveSearch"]') && !event.target.closest('[data-ref="searchButton"]') && document.body.classList.contains('search-is-active')) {
        this.closeSearch();
      }
    };
  }

  connectedCallback() {
    if (this.searchButton) {
      for (let button of this.searchButton) {
        button.addEventListener('click', this._onSearchClick);
      }
    }
    if (this.searchCloseButton) this.searchCloseButton.addEventListener('click', this._onCloseClick);
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    if (this.searchButton) {
      for (let button of this.searchButton) {
        button.removeEventListener('click', this._onSearchClick);
      }
    }
    if (this.searchCloseButton) this.searchCloseButton.removeEventListener('click', this._onCloseClick);
    document.removeEventListener('click', this._onDocClick);
  }

  openSearch() {
    this.predictiveSearch.classList.remove('hidden');
    document.body.classList.add('search-is-active');
    this.searchInput?.focus();
  }

  closeSearch() {
    this.predictiveSearch.classList.add('hidden');
    document.body.classList.remove('search-is-active');
    this.searchInput?.blur();
  }
}

class CollectionCategories extends HTMLElement {
	constructor() {
		super();
    this.categoryButtons = this.querySelectorAll('[data-ref="categoryButtons"]');
    this.carouselWrapper = this.querySelector('[data-ref="carouselWrapper"]');
    this.buttonClickHandler = this.handleButtonclickEvent.bind(this);
	}

	connectedCallback() {
    for (const button of this.categoryButtons) {
      button.addEventListener('click', this.buttonClickHandler);
    }
  }

  disconnectedCallback() {
    for (const button of this.categoryButtons) {
      button.removeEventListener('click', this.buttonClickHandler);
    }
  }

  handleButtonclickEvent(event) {
    const button = event.target.closest('[data-ref="categoryButtons"]');
    if (!button) return;
    
    const blockId = button.getAttribute('data-block-id');
    const templateMarkup = this.querySelector(`#template-${blockId}`);
    this.carouselWrapper.innerHTML = templateMarkup.innerHTML;
    for (const categoryButton of this.categoryButtons) {
      categoryButton.classList.remove('btn-primary');
      categoryButton.classList.add('btn-secondary');
    }
    button.classList.add('btn-primary');
    button.classList.remove('btn-secondary');
  }
}

class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.addButton = this.querySelector('[data-ref="addButton"]');
    this.subtractButton = this.querySelector('[data-ref="subtractButton"]');
    this.input = this.querySelector('[data-ref="quantityInput"]');
    this.message = this.querySelector('[data-ref="message"]');

    // Click handlers
    this.addButtonClickHandler = this.addQuantity.bind(this);
    this.subtractButtonClickHandler = this.subtractQuantity.bind(this);
  }

  connectedCallback() {
    if (this.addButton) {
      this.addButton.addEventListener('click', this.addButtonClickHandler);
    }
    if (this.subtractButton) {
      this.subtractButton.addEventListener('click', this.subtractButtonClickHandler);
    }
  }

  disconnectedCallback() {
    if (this.addButton) {
      this.addButton.removeEventListener('click', this.addButtonClickHandler);
    }
    if (this.subtractButton) {
      this.subtractButton.removeEventListener('click', this.subtractButtonClickHandler);
    }
  }

  get quantity() {
    if (this.input && this.input.value) {
      return parseInt(this.input.value, 10);
    }
    return 1;
  }

  addQuantity(event) {
    const newValue = parseInt(this.input.value) + 1;
    if (!this.input) return;
    if (newValue > parseInt(this.input.max)) {
      if (parseInt(this.input.max) == 1) {
        this.renderError(`There is only ${this.input.max} available`);
      } else {
        this.renderError(`There are only ${this.input.max} available`);
      }
      return;
    }    this.input.value = newValue;
    this.dispatchEvent(
      new CustomEvent(
        'quantity-changed', {
          detail: {
            quantity: this.input.value
          }
        }
      )
    );
  }

  subtractQuantity(event) {
    const newValue = parseInt(this.input.value) - 1;
    if (!this.input || newValue < parseInt(this.input.min)) return;
    this.input.value = newValue;
       this.dispatchEvent(
      new CustomEvent(
        'quantity-changed', {
          detail: {
            quantity: this.input.value
          }
        }
      )
    );
  }

  renderError(message) {
    this.message.innerText = message;
    this.message.classList.remove('hidden');
    setTimeout(() => {
      this.message.classList.add('hidden');
      this.message.innerText = '';
    }, 2000);
  }
}

/*!
 * @license MIT
 * @name vlitejs
 * @version 8.1.1
 * @copyright 2026 Yoriiis
 */
const e={},t={};function s({plugins:s,provider:i,type:n,player:r}){(function(s){const i=[],n=Object.keys(e);return s.forEach((s=>{if(!n.includes(s))throw new Error(`vlitejs :: Unknown plugin "${s}".`);i.push({id:s,Plugin:e[s],options:t[s]});})),i})(s).forEach((({id:e,Plugin:t,options:s})=>{const o=new t({player:r,options:s});if(r.plugins[e]=o,!o.providers.includes(i)||!o.types.includes(n))throw new Error(`vlitejs :: The "${e}" plugin is only compatible with providers:"${o.providers}" and types:"${o.types}"`);o.init();}));}const i={html5:function(e){return class extends e{constructor(e){super(e),this.events=[{type:"timeupdate",listener:super.onTimeUpdate.bind(this)},{type:"ended",listener:super.onMediaEnded.bind(this)},{type:"playing",listener:this.onPlaying.bind(this)},{type:"waiting",listener:this.onWaiting.bind(this)},{type:"seeking",listener:this.onSeeking.bind(this)},{type:"seeked",listener:this.onSeeked.bind(this)}];}init(){this.waitUntilVideoIsReady().then((()=>{this.addSpecificEvents(),super.onReady();}));}waitUntilVideoIsReady(){return new window.Promise((e=>{this.media.readyState>=2&&this.media.duration||0===this.media.readyState&&"none"===this.media.preload?e():(this.media.addEventListener("loadedmetadata",e,{once:true}),this.media.addEventListener("canplay",e,{once:true}));}))}addSpecificEvents(){this.events.forEach((e=>{this.media.addEventListener(e.type,e.listener.bind(this));}));}getInstance(){return this.media}getCurrentTime(){return new window.Promise((e=>e(this.media.currentTime)))}getDuration(){return new window.Promise((e=>e(this.media.duration)))}methodPlay(){this.media.play();}methodPause(){this.media.pause();}methodSetVolume(e){this.media.volume=e;}methodGetVolume(){return new window.Promise((e=>e(this.media.volume)))}methodMute(){this.media.muted=true,this.media.setAttribute("muted","");}methodUnMute(){this.media.muted=false,this.media.removeAttribute("muted");}methodSetSource(e){this.media.src=e,this.media.load(),this.removeSpecificEvents(),this.init();}methodSeekTo(e){this.media.currentTime=e;}onWaiting(){this.loading(true);}onPlaying(){this.loading(false);}onSeeking(){this.loading(true);}onSeeked(){this.loading(false);}removeSpecificEvents(){this.events.forEach((e=>{this.media.removeEventListener(e.type,e.listener);}));}destroy(){this.removeSpecificEvents(),super.destroy();}}}},n={};function r(e){const t=1e3*e,s=t/1e3/60|0,i=t/1e3%60|0;let n="";return n+=s<10?"0":"",n+=`${s}:`,n+=i<10?"0":"",n+=i,n}function o(){const e=function(){if(document.exitFullscreen instanceof Function)return "";return ["webkit","moz","ms"].find((e=>document[`${e}ExitFullscreen`]instanceof Function||document[`${e}CancelFullScreen`]instanceof Function))||""}();return {requestFn:e?`${e}RequestFullScreen`:"requestFullscreen",cancelFn:e?`${e}ExitFullscreen`:"exitFullscreen",changeEvent:e?`${e}fullscreenchange`:"fullscreenchange",isFullScreen:e?`${e}FullscreenElement`:"fullscreenElement"}}function l(){return "ontouchstart"in window||navigator.maxTouchPoints>0}function a({target:e,selectorString:t,nodeName:s}){return (s=[].concat(s)).some((s=>e.nodeName.toLowerCase()===s&&e.matches(t)))}var h='<svg class="v-iconUnpressed" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M1 9c-.55 0-1 .45-1 1v3c0 .55.45 1 1 1h3c.55 0 1-.45 1-1s-.45-1-1-1H2v-2c0-.55-.45-1-1-1Zm0-4c.55 0 1-.45 1-1V2h2c.55 0 1-.45 1-1s-.45-1-1-1H1C.45 0 0 .45 0 1v3c0 .55.45 1 1 1Zm11 7h-2c-.55 0-1 .45-1 1s.45 1 1 1h3c.55 0 1-.45 1-1v-3c0-.55-.45-1-1-1s-1 .45-1 1v2ZM9 1c0 .55.45 1 1 1h2v2c0 .55.45 1 1 1s1-.45 1-1V1c0-.55-.45-1-1-1h-3c-.55 0-1 .45-1 1Z"/></svg>',u='<svg class="v-iconPressed" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M1 11h2v2c0 .55.45 1 1 1s1-.45 1-1v-3c0-.55-.45-1-1-1H1c-.55 0-1 .45-1 1s.45 1 1 1Zm2-8H1c-.55 0-1 .45-1 1s.45 1 1 1h3c.55 0 1-.45 1-1V1c0-.55-.45-1-1-1S3 .45 3 1v2Zm7 11c.55 0 1-.45 1-1v-2h2c.55 0 1-.45 1-1s-.45-1-1-1h-3c-.55 0-1 .45-1 1v3c0 .55.45 1 1 1Zm1-11V1c0-.55-.45-1-1-1S9 .45 9 1v3c0 .55.45 1 1 1h3c.55 0 1-.45 1-1s-.45-1-1-1h-2Z"/></svg>',c='<svg class="v-iconPressed" viewBox="0 0 12 14" xmlns="http://www.w3.org/2000/svg"><path d="M2 14c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2S0 .9 0 2v10c0 1.1.9 2 2 2ZM8 2v10c0 1.1.9 2 2 2s2-.9 2-2V2c0-1.1-.9-2-2-2S8 .9 8 2Z"/></svg>',d='<svg class="v-iconUnpressed" viewBox="0 0 11 13" xmlns="http://www.w3.org/2000/svg"><path d="M0 1v10.36c0 .79.87 1.27 1.54.84l8.14-5.18a1 1 0 0 0 0-1.69L1.54.16A.998.998 0 0 0 0 1Z"/></svg>',v='<svg class="v-iconUnpressed" viewBox="0 0 18 17" xmlns="http://www.w3.org/2000/svg"><path d="M0 6.461v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71V2.871c0-.89-1.08-1.34-1.71-.71L4 5.461H1c-.55 0-1 .45-1 1Zm13.5 2a4.5 4.5 0 0 0-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02ZM11 .911v.2c0 .38.25.71.6.85a7.004 7.004 0 0 1 4.4 6.5c0 2.94-1.82 5.47-4.4 6.5-.36.14-.6.47-.6.85v.2c0 .63.63 1.07 1.21.85a8.98 8.98 0 0 0 5.79-8.4c0-3.84-2.4-7.11-5.79-8.4-.58-.23-1.21.22-1.21.85Z"/></svg>',p='<svg class="v-iconPressed" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg"><path d="M.63.292a.996.996 0 0 0 0 1.41l3.66 3.66-.29.3H1c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91.8-.33 1.55-.77 2.22-1.31l1.34 1.34a.997.997 0 1 0 1.41-1.41L2.05.293c-.39-.39-1.02-.39-1.42 0ZM16 8.662c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85 2.57 1.04 4.39 3.56 4.39 6.5Zm-8.71-6.29-.17.17L9 4.422v-1.35c0-.89-1.08-1.33-1.71-.7Zm6.21 6.29a4.5 4.5 0 0 0-2.5-4.03v1.79l2.48 2.48c.01-.08.02-.16.02-.24Z"/></svg>';function m({options:e,isMuted:t,isVideo:s}){return s?`<div class="v-controlBar">${e.playPause?y():""}${e.time?g():""}${e.progressBar?P():""}${e.volume?f({isMuted:t}):""}${e.fullscreen?`<button class="v-fullscreenButton v-controlButton" aria-label="Enter fullscreen">${h}${u}</span></button>`:""}</div>`:`<div class="v-controlBar">${e.playPause?y():""}${e.time?g():""}${e.progressBar?P():""}${e.volume?f({isMuted:t}):""}</div>`}function y(){return `<button class="v-playPauseButton v-controlButton" aria-label="Play">${d}${c}</button>`}function g(){return '<div class="v-time"><span class="v-currentTime">00:00</span>&nbsp;/&nbsp;<span class="v-duration"></span></div>'}function P(){return '<input type="range" class="v-progressBar v-progressBarStyle" min="0" max="100" step="0.01" value="0" aria-label="Seek" aria-valuemin="0" />'}function f({isMuted:e}){return `<button class="v-volumeButton v-controlButton${e?" v-controlPressed":""}">${v}${p}</button>`}class b{constructor({player:e,type:t}){this.player=e,this.type=t,this.touchEvents=["touchstart","touchmove","touchend"],this.onInputProgressBar=this.onInputProgressBar.bind(this),this.onTouchEventProgressBar=this.onTouchEventProgressBar.bind(this),this.onClickOnControlBar=this.onClickOnControlBar.bind(this),this.togglePlayPause=this.togglePlayPause.bind(this),this.toggleVolume=this.toggleVolume.bind(this),this.toggleFullscreen=this.toggleFullscreen.bind(this);}init(){this.render(),this.cacheElements(),this.addEvents();}cacheElements(){const e=this.player.elements.container.querySelector(".v-controlBar");e&&(this.player.elements.controlBar=e,this.player.elements.playPause=e.querySelector(".v-playPauseButton"),this.player.elements.progressBar=e.querySelector(".v-progressBar"),this.player.elements.currentTime=e.querySelector(".v-currentTime"),this.player.elements.duration=e.querySelector(".v-duration"),this.player.elements.volume=e.querySelector(".v-volumeButton"),this.player.elements.fullscreen=e.querySelector(".v-fullscreenButton"),this.player.elements.volume&&this.player.elements.volume.setAttribute("aria-label",this.player.isMuted?"Unmute":"Mute"));}render(){this.player.elements.container.insertAdjacentHTML("beforeend",this.getTemplate());}onReady(){this.player.getDuration().then((e=>{this.player.elements.progressBar&&this.player.elements.progressBar.setAttribute("aria-valuemax",`${Math.round(e)}`),this.player.elements.duration&&(this.player.elements.duration.innerHTML=r(e));}));}addEvents(){var e;this.player.elements.progressBar&&(this.player.elements.progressBar.addEventListener("input",this.onInputProgressBar),this.player.isTouch&&this.touchEvents.forEach((e=>{this.player.elements.progressBar.addEventListener(e,this.onTouchEventProgressBar);}))),null===(e=this.player.elements.controlBar)||void 0===e||e.addEventListener("click",this.onClickOnControlBar);}onTouchEventProgressBar(e){e.preventDefault();const t=e.target,s=Number.parseFloat(t.getAttribute("max")||"100"),i=t.getBoundingClientRect(),n=(e.changedTouches[0].clientX-i.left)/i.width*100;t.value=""+100*n/s,t.dispatchEvent(new Event("input"));}onInputProgressBar(e){const t=e.target;t.style.setProperty("--vlite-progressValue",`${t.value}%`),this.player.elements.outerContainer.classList.contains("v-firstStart")&&this.player.play(),this.player.getDuration().then((e=>{this.player.seekTo(Number.parseFloat(t.value)*e/100);}));}onClickOnControlBar(e){const t=e.target,s=a({target:t,selectorString:".v-playPauseButton",nodeName:["button"]}),i=a({target:t,selectorString:".v-volumeButton",nodeName:["button"]}),n=a({target:t,selectorString:".v-fullscreenButton",nodeName:["button"]});s?this.togglePlayPause(e):i?this.toggleVolume(e):n&&this.toggleFullscreen(e),(s||i||n)&&this.player.elements.container.focus(),this.player.Vlitejs.startAutoHideTimer();}togglePlayPause(e){e.preventDefault(),this.player.elements.outerContainer.classList.contains("v-paused")?this.player.play():this.player.pause();}toggleVolume(e){e.preventDefault(),this.player.elements.volume.classList.contains("v-controlPressed")?this.player.unMute():this.player.mute();}toggleFullscreen(e){if(e.preventDefault(),this.player.isFullScreen)this.isOrientationApiAvailable()&&window.screen.orientation.unlock(),this.player.exitFullscreen();else {this.player.requestFullscreen();try{window.matchMedia("(orientation:portrait)").matches&&this.isOrientationApiAvailable()&&window.screen.orientation.lock("landscape").catch((()=>{}));}catch(e){}}}isOrientationApiAvailable(){return void 0!==window.screen.orientation&&"function"==typeof window.screen.orientation.lock&&"function"==typeof window.screen.orientation.unlock}getTemplate(){return `${m({options:this.player.options,isMuted:this.player.isMuted,isVideo:"video"===this.type})}`}removeEvents(){var e;this.player.elements.progressBar&&(this.player.elements.progressBar.removeEventListener("input",this.onInputProgressBar),this.player.isTouch&&this.touchEvents.forEach((e=>{this.player.elements.progressBar.removeEventListener(e,this.onTouchEventProgressBar);}))),null===(e=this.player.elements.controlBar)||void 0===e||e.removeEventListener("click",this.onClickOnControlBar);}destroy(){var e;this.removeEvents(),null===(e=this.player.elements.controlBar)||void 0===e||e.remove();}}class w{constructor({Vlitejs:e,type:t}){this.Vlitejs=e,this.type=t,this.plugins={},this.media=e.media,this.options=e.options,this.isCast=false,this.isLinearAd=false,this.elements={container:e.container,outerContainer:e.outerContainer,bigPlay:e.container.querySelector(".v-bigPlay"),poster:e.container.querySelector(".v-poster"),controlBar:null,playPause:null,progressBar:null,currentTime:null,duration:null,volume:null,fullscreen:null},this.isFullScreen=false,this.isMuted=this.options.muted,this.isPaused=null,this.playerEvents=[],this.isTouch=l(),this.controlBar=new b({player:this,type:t});}build(){this.options.controls&&this.controlBar.init(),this.init();}onReady(){this.options.muted&&this.mute(),this.media.classList.add("v-media"),this.media.setAttribute("tabindex","-1"),this.options.autoplay&&(!this.media.muted&&this.mute(),this.play()),this.Vlitejs.onReady instanceof Function&&this.Vlitejs.onReady.call(this,this),"none"===this.media.preload&&"html5"===this.Vlitejs.provider?this.media.addEventListener("loadedmetadata",(()=>this.triggerOnReady())):this.triggerOnReady(),this.loading(false);}triggerOnReady(){this.options.controls&&this.controlBar.onReady(),Object.keys(this.plugins).forEach((e=>{this.plugins[e].onReady instanceof Function&&this.plugins[e].onReady();}));}on(e,t){t instanceof Function&&(this.playerEvents.push({type:e,listener:t}),this.elements.container.addEventListener(e,t));}off(e,t){t instanceof Function&&this.elements.container.removeEventListener(e,t);}dispatchEvent(e,t){this.elements.container.dispatchEvent(new window.CustomEvent(e,{detail:t}));}loading(e){this.elements.outerContainer.classList[e?"add":"remove"]("v-loading"),this.dispatchEvent("progress");}onTimeUpdate(){this.options.time&&Promise.all([this.getCurrentTime(),this.getDuration()]).then((([e,t])=>this.updateProgressBar({seconds:e,duration:t})));}updateProgressBar({seconds:e,duration:t,isRemote:s=false}){const i=Math.round(e);if(this.elements.progressBar){const s=100*i/t;this.elements.progressBar.value=`${s}`,this.elements.progressBar.style.setProperty("--vlite-progressValue",`${s}%`),this.elements.progressBar.setAttribute("aria-valuenow",`${Math.round(e)}`);}this.elements.currentTime&&(this.elements.currentTime.innerHTML=r(i)),!s&&this.dispatchEvent("timeupdate");}onMediaEnded(){this.options.loop?this.play():(this.elements.outerContainer.classList.replace("v-playing","v-paused"),this.elements.outerContainer.classList.add("v-firstStart"),this.isPaused=null),this.elements.poster&&this.elements.poster.classList.add("v-active"),"video"===this.type&&this.elements.bigPlay&&this.elements.bigPlay.setAttribute("aria-label","Play"),this.elements.playPause&&(this.elements.playPause.setAttribute("aria-label","Play"),this.elements.playPause.classList.remove("v-controlPressed")),this.elements.progressBar&&(this.elements.progressBar.value="0",this.elements.progressBar.style.setProperty("--vlite-progressValue","0%"),this.elements.progressBar.removeAttribute("aria-valuenow")),this.elements.currentTime&&(this.elements.currentTime.innerHTML="00:00"),this.dispatchEvent("ended");}play(){this.isLinearAd||(null===this.isPaused&&(this.elements.outerContainer.classList.remove("v-firstStart"),"video"===this.type&&this.elements.poster&&this.elements.poster.classList.remove("v-active")),!this.isCast&&this.methodPlay(),this.isPaused=false,this.elements.outerContainer.classList.replace("v-paused","v-playing"),this.elements.playPause&&(this.elements.playPause.setAttribute("aria-label","Pause"),this.elements.playPause.classList.add("v-controlPressed")),"video"===this.type&&this.elements.bigPlay&&this.elements.bigPlay.setAttribute("aria-label","Pause"),this.afterPlayPause(),this.dispatchEvent("play"));}pause(){!this.isCast&&this.methodPause(),this.isPaused=true,this.elements.outerContainer.classList.replace("v-playing","v-paused"),this.elements.playPause&&(this.elements.playPause.setAttribute("aria-label","Play"),this.elements.playPause.classList.remove("v-controlPressed")),"video"===this.type&&this.elements.bigPlay&&this.elements.bigPlay.setAttribute("aria-label","Play"),this.afterPlayPause(),this.dispatchEvent("pause");}afterPlayPause(){this.Vlitejs.autoHideGranted&&(this.Vlitejs.stopAutoHideTimer(),!this.isPaused&&this.Vlitejs.startAutoHideTimer());}setVolume(e){let t=e;t>1?t=1:t<=0?(t=0,this.isMuted=true,this.elements.volume&&this.elements.volume.classList.add("v-controlPressed")):(this.isMuted=false,this.elements.volume&&this.elements.volume.classList.remove("v-controlPressed")),this.methodSetVolume(t),this.dispatchEvent("volumechange",{volume:t});}getVolume(){return new window.Promise((e=>{this.methodGetVolume().then((t=>{e(t);}));}))}mute(){this.methodMute(),this.isMuted=true,this.elements.volume&&(this.elements.volume.classList.add("v-controlPressed"),this.elements.volume.setAttribute("aria-label","Unmute")),this.dispatchEvent("volumechange");}unMute(){this.methodUnMute(),this.isMuted=false,this.elements.volume&&(this.elements.volume.classList.remove("v-controlPressed"),this.elements.volume.setAttribute("aria-label","Mute")),this.dispatchEvent("volumechange");}setSource(e){this.options.loop=false,this.pause(),this.onMediaEnded(),this.methodSetSource(e),this.controlBar.onReady(),this.play(),this.dispatchEvent("sourcechange");}seekTo(e){this.methodSeekTo(e);}requestFullscreen(){const{requestFn:e}=this.Vlitejs.supportFullScreen;this.media[e]&&(this.elements.container[e](),this.isFullScreen=true,this.elements.outerContainer.classList.add("v-fullscreen"),this.elements.fullscreen&&(this.elements.fullscreen.classList.add("v-controlPressed"),this.elements.fullscreen.setAttribute("aria-label","Exit fullscreen")),this.dispatchEvent("enterfullscreen"));}exitFullscreen({escKey:e=false}={}){const{cancelFn:t}=this.Vlitejs.supportFullScreen;document[t]&&(!e&&document[t](),this.isFullScreen=false,this.elements.outerContainer.classList.remove("v-fullscreen"),this.elements.fullscreen&&(this.elements.fullscreen.classList.remove("v-controlPressed"),this.elements.fullscreen.setAttribute("aria-label","Enter fullscreen")),this.dispatchEvent("exitfullscreen"));}destroy(){var e;Object.keys(this.plugins).forEach((e=>{this.plugins[e].destroy instanceof Function&&this.plugins[e].destroy();})),null===(e=this.controlBar)||void 0===e||e.destroy(),this.playerEvents.forEach((e=>{this.elements.container.removeEventListener(e.type,e.listener);})),this.elements.container.remove();}}const E={audio:{controls:true,autoplay:false,playPause:true,progressBar:true,time:true,volume:true,loop:false},video:{controls:true,autoplay:false,playPause:true,progressBar:true,time:true,volume:true,fullscreen:true,poster:null,bigPlay:true,playsinline:true,loop:false,muted:false,autoHide:false,autoHideDelay:3e3,providerParams:{}}};class B{constructor(e,{options:t={},provider:r="html5",plugins:l=[],onReady:a=()=>{}}={}){if("string"==typeof e)this.media=document.querySelector(e);else {if(!(e instanceof HTMLVideoElement||e instanceof HTMLAudioElement||e instanceof HTMLDivElement))throw new TypeError("vlitejs :: The element or selector supplied is not valid.");this.media=e;}this.provider=r,this.onReady=a,this.type=this.media instanceof HTMLAudioElement?"audio":"video",this.supportFullScreen=o();["autoplay","playsinline","muted","loop"].forEach((e=>{this.media.hasAttribute(e)?t[e]=true:t[e]&&this.media.setAttribute(e,"");})),this.options=Object.assign(Object.assign({},E[this.type]),t),this.autoHideGranted="video"===this.type&&!!this.options.autoHide&&!!this.options.controls,/(iPhone)/gi.test(window.navigator.userAgent)&&(this.options.fullscreen=false),this.onClickOnPlayer=this.onClickOnPlayer.bind(this),this.onDoubleClickOnPlayer=this.onDoubleClickOnPlayer.bind(this),this.onMousemove=this.onMousemove.bind(this),this.onChangeFullScreen=this.onChangeFullScreen.bind(this);const h=function(e,t){const s=i[e];if(s)return s(t,n[e]);throw new Error(`vlitejs :: Unknown provider "${e}"`)}(r,w);this.wrapElement(),this.container=this.media.closest(".v-container"),this.outerContainer=this.container.closest(".v-vlite"),"video"===this.type&&this.renderLayout(),this.player=new h({type:this.type,Vlitejs:this}),this.player.build(),this.addEvents(),s({plugins:l,provider:r,type:this.type,player:this.player});}wrapElement(){const e=document.createElement("div");e.classList.add("v-vlite","v-firstStart","v-paused","v-loading",`v-${this.type}`);const t=document.createElement("div");t.setAttribute("tabindex","0"),t.classList.add("v-container"),e.appendChild(t);this.media.parentNode.insertBefore(e,this.media),t.appendChild(this.media);}renderLayout(){const e=`\n\t\t\t<div class="v-overlay"></div>\n\t\t\t\n\t\t<div class="v-loader">\n\t\t\t<div class="v-loaderContent">\n\t\t\t\t<div class="v-loaderBounce1"></div>\n\t\t\t\t<div class="v-loaderBounce2"></div>\n\t\t\t\t<div class="v-loaderBounce3"></div>\n\t\t\t</div>\n\t\t</div>\n\t\n\t\t\t${this.options.poster?function({posterUrl:e=""}){return `<div class="v-poster v-active"${e&&` style="background-image: url(${e})"`}></div>`}({posterUrl:this.options.poster}):""}\n\t\t\t${this.options.bigPlay?'<button class="v-bigPlay" aria-label="Play"><svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0ZM7.5 12.67V7.33c0-.79.88-1.27 1.54-.84l4.15 2.67a1 1 0 0 1 0 1.68l-4.15 2.67c-.66.43-1.54-.05-1.54-.84Z"/></svg></button>':""}\n\t\t`;this.container.insertAdjacentHTML("beforeend",e);}addEvents(){"video"===this.type&&(this.container.addEventListener("click",this.onClickOnPlayer),this.container.addEventListener("dblclick",this.onDoubleClickOnPlayer),this.autoHideGranted&&!this.player.isTouch&&this.container.addEventListener("mousemove",this.onMousemove),document.addEventListener(this.supportFullScreen.changeEvent,this.onChangeFullScreen));}onClickOnPlayer(e){var t;const s=e.target,i=a({target:s,selectorString:".v-poster, .v-bigPlay",nodeName:["div","button"]}),n=a({target:s,selectorString:".v-overlay",nodeName:["div"]});if(n&&l()&&this.autoHideGranted&&(null===(t=this.player.elements.controlBar)||void 0===t?void 0:t.classList.contains("v-hidden")))return this.stopAutoHideTimer(),void this.startAutoHideTimer();(i||n&&!l())&&(this.player.controlBar.togglePlayPause(e),s.matches(".v-bigPlay")&&this.container.focus());}onDoubleClickOnPlayer(e){a({target:e.target,selectorString:".v-overlay",nodeName:["div"]})&&this.player.controlBar.toggleFullscreen(e);}onMousemove(){!this.player.isPaused&&this.autoHideGranted&&(this.stopAutoHideTimer(),this.startAutoHideTimer());}onChangeFullScreen(){!document[this.supportFullScreen.isFullScreen]&&this.player.isFullScreen&&this.player.exitFullscreen({escKey:true});}stopAutoHideTimer(){var e;"video"===this.type&&this.player.elements.controlBar&&(null===(e=this.player.elements.controlBar)||void 0===e||e.classList.remove("v-hidden"),clearTimeout(this.timerAutoHide));}startAutoHideTimer(){"video"===this.type&&!this.player.isPaused&&this.player.elements.controlBar&&(this.timerAutoHide=window.setTimeout((()=>{var e;null===(e=this.player.elements.controlBar)||void 0===e||e.classList.add("v-hidden");}),this.options.autoHideDelay));}removeEvents(){"video"===this.type&&(this.container.removeEventListener("click",this.onClickOnPlayer),this.container.removeEventListener("dblclick",this.onDoubleClickOnPlayer),this.autoHideGranted&&!this.player.isTouch&&this.container.removeEventListener("mousemove",this.onMousemove),window.removeEventListener(this.supportFullScreen.changeEvent,this.onChangeFullScreen));}destroy(){this.removeEvents(),this.player.destroy(),this.player.controlBar.destroy();}}B.registerProvider=function(e,t,s){if(void 0!==t){if(!Object.keys(i).includes(e))return i[e]=t,void(s&&(n[e]=s));throw new Error(`vlitejs :: The provider id "${e}" is already registered.`)}throw new Error(`vlitejs :: The provider id "${e}" is undefined.`)},B.registerPlugin=function(s,i,n){if(void 0!==i){if(!Object.keys(e).includes(s))return e[s]=i,void(n&&(t[s]=n));throw new Error(`vlitejs :: The plugin id "${s}" is already registered.`)}throw new Error(`vlitejs :: The plugin id "${s}" is undefined.`)};

class VideoPlayer extends HTMLElement {
  constructor() {
    super();
    this.vliteInstance = null;
    this.intersectionObserver = null;
    this.player = null;
    this.videoElement = null;
    this.isIntersecting = false;
    this.manuallyPaused = false;
    this.viewportPausePending = false;
    this.autoplay = this.getAttribute('autoplay') === 'true';
    this.customControls = this.getAttribute('controls') === 'false';
    this.loop = this.getAttribute('loop') === 'true';
    this.playIcon = null;
    this.clickHandler = this.handleClick.bind(this);
    this.playHandler = this.handlePlay.bind(this);
    this.pauseHandler = this.handlePause.bind(this);
  }
  
  connectedCallback() {
    const video = this.querySelector('video');
    if (video) {
      this.videoElement = video;
      this.playIcon = this.querySelector('.product-gallery-video__play-icon');
      const poster = this.getAttribute('poster');
      if (this.customControls) {
        this.addEventListener('click', this.clickHandler, true);
      }
      video.addEventListener('play', this.playHandler);
      video.addEventListener('pause', this.pauseHandler);

      if (this.autoplay) {
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
          this.isIntersecting = entry.isIntersecting;
          if (this.isIntersecting && this.player && !this.manuallyPaused) {
            this.player.play();
          } else if (!this.isIntersecting && this.player && !this.videoElement.paused) {
            this.viewportPausePending = true;
            this.player.pause();
          }
        });
        this.intersectionObserver.observe(this);
      }

      this.vliteInstance = new B(video, {
        options: {
          autoplay: false,
          controls: !this.customControls,
          bigPlay: !this.customControls,
          muted: this.autoplay,
          loop: this.loop,
          poster: poster || null
        },
        onReady: (player) => {
          this.player = player;
          if (this.autoplay && this.isIntersecting && !this.manuallyPaused) {
            player.play();
          }
        }
      });
    }
  }

  togglePlayback() {
    if (!this.videoElement) return;

    if (!this.videoElement.paused) {
      this.manuallyPaused = true;
      this.videoElement.pause();
    } else {
      this.manuallyPaused = false;
      this.videoElement.play();
    }
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.togglePlayback();
  }

  handlePlay() {
    this.manuallyPaused = false;
    this.viewportPausePending = false;
    this.playIcon?.setAttribute('hidden', '');
  }

  handlePause() {
    this.playIcon?.removeAttribute('hidden');
    if (this.viewportPausePending) {
      this.viewportPausePending = false;
    } else if (this.autoplay) {
      this.manuallyPaused = true;
    }
  }
  
  disconnectedCallback() {
    this.removeEventListener('click', this.clickHandler, true);
    this.videoElement?.removeEventListener('play', this.playHandler);
    this.videoElement?.removeEventListener('pause', this.pauseHandler);
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    if (this.vliteInstance && this.vliteInstance.destroy) {
      this.vliteInstance.destroy();
    }
    this.vliteInstance = null;
    this.player = null;
    this.videoElement = null;
    this.isIntersecting = false;
    this.manuallyPaused = false;
    this.viewportPausePending = false;
  }
}

function noop() { }
const identity = x => x;
function assign(tar, src) {
    // @ts-ignore
    for (const k in src)
        tar[k] = src[k];
    return tar;
}
function run(fn) {
    return fn();
}
function run_all(fns) {
    fns.forEach(run);
}
function is_function(thing) {
    return typeof thing === 'function';
}
function not_equal(a, b) {
    return a != a ? b == b : a !== b;
}
function is_empty(obj) {
    return Object.keys(obj).length === 0;
}
function subscribe(store, ...callbacks) {
    if (store == null) {
        return noop;
    }
    const unsub = store.subscribe(...callbacks);
    return unsub.unsubscribe ? () => unsub.unsubscribe() : unsub;
}
function component_subscribe(component, store, callback) {
    component.$$.on_destroy.push(subscribe(store, callback));
}
function action_destroyer(action_result) {
    return action_result && is_function(action_result.destroy) ? action_result.destroy : noop;
}
let now = () => globalThis.performance.now()
    ;
let raf = cb => requestAnimationFrame(cb) ;

const tasks = new Set();
function run_tasks(now) {
    tasks.forEach(task => {
        if (!task.c(now)) {
            tasks.delete(task);
            task.f();
        }
    });
    if (tasks.size !== 0)
        raf(run_tasks);
}
/**
 * Creates a new task that runs on each raf frame
 * until it returns a falsy value or is aborted
 */
function loop(callback) {
    let task;
    if (tasks.size === 0)
        raf(run_tasks);
    return {
        promise: new Promise(fulfill => {
            tasks.add(task = { c: callback, f: fulfill });
        }),
        abort() {
            tasks.delete(task);
        }
    };
}
function append(target, node) {
    target.appendChild(node);
}
function insert(target, node, anchor) {
    target.insertBefore(node, anchor || null);
}
function detach(node) {
    node.parentNode.removeChild(node);
}
function element(name) {
    return document.createElement(name);
}
function text(data) {
    return document.createTextNode(data);
}
function empty() {
    return text('');
}
function listen(node, event, handler, options) {
    node.addEventListener(event, handler, options);
    return () => node.removeEventListener(event, handler, options);
}
function attr(node, attribute, value) {
    if (value == null)
        node.removeAttribute(attribute);
    else if (node.getAttribute(attribute) !== value)
        node.setAttribute(attribute, value);
}
function set_style(node, key, value, important) {
    if (value === null) {
        node.style.removeProperty(key);
    }
    else {
        node.style.setProperty(key, value);
    }
}
function toggle_class(element, name, toggle) {
    element.classList[toggle ? 'add' : 'remove'](name);
}
function custom_event(type, detail, bubbles = false) {
    const e = document.createEvent('CustomEvent');
    e.initCustomEvent(type, bubbles, false, detail);
    return e;
}
let stylesheet;
let active = 0;
let current_rules = {};
// https://github.com/darkskyapp/string-hash/blob/master/index.js
// function hash(str) {
//     let hash = 5381;
//     let i = str.length;
//     while (i--)
//         hash = ((hash << 5) - hash) ^ str.charCodeAt(i);
//     return hash >>> 0;
// }
function create_rule(node, a, b, duration, delay, ease, fn, uid = 0) {
    const step = 16.666 / duration;
    let keyframes = '{\n';
    for (let p = 0; p <= 1; p += step) {
        const t = a + (b - a) * ease(p);
        keyframes += p * 100 + `%{${fn(t, 1 - t)}}\n`;
    }
    const rule = keyframes + `100% {${fn(b, 1 - b)}}\n}`;
    const name = `_bp_${Math.round(Math.random() * 1e9)}_${uid}`;
    if (!current_rules[name]) {
        if (!stylesheet) {
            const style = element('style');
            document.head.appendChild(style);
            stylesheet = style.sheet;
        }
        current_rules[name] = true;
        stylesheet.insertRule(`@keyframes ${name} ${rule}`, stylesheet.cssRules.length);
    }
    const animation = node.style.animation || '';
    node.style.animation = `${animation ? `${animation}, ` : ``}${name} ${duration}ms linear ${delay}ms 1 both`;
    active += 1;
    return name;
}
function delete_rule(node, name) {
    node.style.animation = (node.style.animation || '')
        .split(', ')
        .filter(name
        ? anim => anim.indexOf(name) < 0 // remove specific animation
        : anim => anim.indexOf('_bp') === -1 // remove all Svelte animations
    )
        .join(', ');
    if (name && !--active)
        clear_rules();
}
function clear_rules() {
    raf(() => {
        if (active)
            return;
        let i = stylesheet.cssRules.length;
        while (i--)
            stylesheet.deleteRule(i);
        current_rules = {};
    });
}

let current_component;
function set_current_component(component) {
    current_component = component;
}

const dirty_components = [];
const binding_callbacks = [];
const render_callbacks = [];
const flush_callbacks = [];
const resolved_promise = Promise.resolve();
let update_scheduled = false;
function schedule_update() {
    if (!update_scheduled) {
        update_scheduled = true;
        resolved_promise.then(flush);
    }
}
function add_render_callback(fn) {
    render_callbacks.push(fn);
}
// flush() calls callbacks in this order:
// 1. All beforeUpdate callbacks, in order: parents before children
// 2. All bind:this callbacks, in reverse order: children before parents.
// 3. All afterUpdate callbacks, in order: parents before children. EXCEPT
//    for afterUpdates called during the initial onMount, which are called in
//    reverse order: children before parents.
// Since callbacks might update component values, which could trigger another
// call to flush(), the following steps guard against this:
// 1. During beforeUpdate, any updated components will be added to the
//    dirty_components array and will cause a reentrant call to flush(). Because
//    the flush index is kept outside the function, the reentrant call will pick
//    up where the earlier call left off and go through all dirty components. The
//    current_component value is saved and restored so that the reentrant call will
//    not interfere with the "parent" flush() call.
// 2. bind:this callbacks cannot trigger new flush() calls.
// 3. During afterUpdate, any updated components will NOT have their afterUpdate
//    callback called a second time; the seen_callbacks set, outside the flush()
//    function, guarantees this behavior.
const seen_callbacks = new Set();
let flushidx = 0; // Do *not* move this inside the flush() function
function flush() {
    const saved_component = current_component;
    do {
        // first, call beforeUpdate functions
        // and update components
        while (flushidx < dirty_components.length) {
            const component = dirty_components[flushidx];
            flushidx++;
            set_current_component(component);
            update(component.$$);
        }
        set_current_component(null);
        dirty_components.length = 0;
        flushidx = 0;
        while (binding_callbacks.length)
            binding_callbacks.pop()();
        // then, once components are updated, call
        // afterUpdate functions. This may cause
        // subsequent updates...
        for (let i = 0; i < render_callbacks.length; i += 1) {
            const callback = render_callbacks[i];
            if (!seen_callbacks.has(callback)) {
                // ...so guard against infinite loops
                seen_callbacks.add(callback);
                callback();
            }
        }
        render_callbacks.length = 0;
    } while (dirty_components.length);
    while (flush_callbacks.length) {
        flush_callbacks.pop()();
    }
    update_scheduled = false;
    seen_callbacks.clear();
    set_current_component(saved_component);
}
function update($$) {
    if ($$.fragment !== null) {
        $$.update();
        run_all($$.before_update);
        const dirty = $$.dirty;
        $$.dirty = [-1];
        $$.fragment && $$.fragment.p($$.ctx, dirty);
        $$.after_update.forEach(add_render_callback);
    }
}

let promise;
function wait() {
    if (!promise) {
        promise = Promise.resolve();
        promise.then(() => {
            promise = null;
        });
    }
    return promise;
}
function dispatch(node, direction, kind) {
    node.dispatchEvent(custom_event(`${direction ? 'intro' : 'outro'}${kind}`));
}
const outroing = new Set();
let outros;
function group_outros() {
    outros = {
        r: 0,
        c: [],
        p: outros // parent group
    };
}
function check_outros() {
    if (!outros.r) {
        run_all(outros.c);
    }
    outros = outros.p;
}
function transition_in(block, local) {
    if (block && block.i) {
        outroing.delete(block);
        block.i(local);
    }
}
function transition_out(block, local, detach, callback) {
    if (block && block.o) {
        if (outroing.has(block))
            return;
        outroing.add(block);
        outros.c.push(() => {
            outroing.delete(block);
            if (callback) {
                if (detach)
                    block.d(1);
                callback();
            }
        });
        block.o(local);
    }
}
const null_transition = { duration: 0 };
function create_in_transition(node, fn, params) {
    let config = fn(node, params);
    let running = false;
    let animation_name;
    let task;
    let uid = 0;
    function cleanup() {
        if (animation_name)
            delete_rule(node, animation_name);
    }
    function go() {
        const { delay = 0, duration = 300, easing = identity, tick = noop, css } = config || null_transition;
        if (css)
            animation_name = create_rule(node, 0, 1, duration, delay, easing, css, uid++);
        tick(0, 1);
        const start_time = now() + delay;
        const end_time = start_time + duration;
        if (task)
            task.abort();
        running = true;
        add_render_callback(() => dispatch(node, true, 'start'));
        task = loop(now => {
            if (running) {
                if (now >= end_time) {
                    tick(1, 0);
                    dispatch(node, true, 'end');
                    cleanup();
                    return running = false;
                }
                if (now >= start_time) {
                    const t = easing((now - start_time) / duration);
                    tick(t, 1 - t);
                }
            }
            return running;
        });
    }
    let started = false;
    return {
        start() {
            if (started)
                return;
            started = true;
            delete_rule(node);
            if (is_function(config)) {
                config = config();
                wait().then(go);
            }
            else {
                go();
            }
        },
        invalidate() {
            started = false;
        },
        end() {
            if (running) {
                cleanup();
                running = false;
            }
        }
    };
}
function create_out_transition(node, fn, params) {
    let config = fn(node, params);
    let running = true;
    let animation_name;
    const group = outros;
    group.r += 1;
    function go() {
        const { delay = 0, duration = 300, easing = identity, tick = noop, css } = config || null_transition;
        if (css)
            animation_name = create_rule(node, 1, 0, duration, delay, easing, css);
        const start_time = now() + delay;
        const end_time = start_time + duration;
        add_render_callback(() => dispatch(node, false, 'start'));
        loop(now => {
            if (running) {
                if (now >= end_time) {
                    tick(0, 1);
                    dispatch(node, false, 'end');
                    if (!--group.r) {
                        // this will result in `end()` being called,
                        // so we don't need to clean up here
                        run_all(group.c);
                    }
                    return false;
                }
                if (now >= start_time) {
                    const t = easing((now - start_time) / duration);
                    tick(1 - t, t);
                }
            }
            return running;
        });
    }
    if (is_function(config)) {
        wait().then(() => {
            // @ts-ignore
            config = config();
            go();
        });
    }
    else {
        go();
    }
    return {
        end(reset) {
            if (reset && config.tick) {
                config.tick(1, 0);
            }
            if (running) {
                if (animation_name)
                    delete_rule(node, animation_name);
                running = false;
            }
        }
    };
}
function create_component(block) {
    block && block.c();
}
function mount_component(component, target, anchor, customElement) {
    const { fragment, on_mount, on_destroy, after_update } = component.$$;
    fragment && fragment.m(target, anchor);
    if (!customElement) {
        // onMount happens before the initial afterUpdate
        add_render_callback(() => {
            const new_on_destroy = on_mount.map(run).filter(is_function);
            if (on_destroy) {
                on_destroy.push(...new_on_destroy);
            }
            else {
                // Edge case - component was destroyed immediately,
                // most likely as a result of a binding initialising
                run_all(new_on_destroy);
            }
            component.$$.on_mount = [];
        });
    }
    after_update.forEach(add_render_callback);
}
function destroy_component(component, detaching) {
    const $$ = component.$$;
    if ($$.fragment !== null) {
        run_all($$.on_destroy);
        $$.fragment && $$.fragment.d(detaching);
        // TODO null out other refs, including component.$$ (but need to
        // preserve final state?)
        $$.on_destroy = $$.fragment = null;
        $$.ctx = [];
    }
}
function make_dirty(component, i) {
    if (component.$$.dirty[0] === -1) {
        dirty_components.push(component);
        schedule_update();
        component.$$.dirty.fill(0);
    }
    component.$$.dirty[(i / 31) | 0] |= (1 << (i % 31));
}
function init(component, options, instance, create_fragment, not_equal, props, append_styles, dirty = [-1]) {
    const parent_component = current_component;
    set_current_component(component);
    const $$ = component.$$ = {
        fragment: null,
        ctx: null,
        // state
        props,
        update: noop,
        not_equal,
        bound: {},
        // lifecycle
        on_mount: [],
        on_destroy: [],
        on_disconnect: [],
        before_update: [],
        after_update: [],
        context: new Map(options.context || (parent_component ? parent_component.$$.context : [])),
        // everything else
        callbacks: {},
        dirty,
        skip_bound: false,
        root: options.target || parent_component.$$.root
    };
    append_styles && append_styles($$.root);
    let ready = false;
    $$.ctx = instance
        ? instance(component, options.props || {}, (i, ret, ...rest) => {
            const value = rest.length ? rest[0] : ret;
            if ($$.ctx && not_equal($$.ctx[i], $$.ctx[i] = value)) {
                if (!$$.skip_bound && $$.bound[i])
                    $$.bound[i](value);
                if (ready)
                    make_dirty(component, i);
            }
            return ret;
        })
        : [];
    $$.update();
    ready = true;
    run_all($$.before_update);
    // `false` as a special case of no DOM component
    $$.fragment = create_fragment ? create_fragment($$.ctx) : false;
    if (options.target) {
        {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            $$.fragment && $$.fragment.c();
        }
        mount_component(component, options.target, options.anchor, options.customElement);
        flush();
    }
    set_current_component(parent_component);
}
/**
 * Base class for Svelte components. Used when dev=false.
 */
class SvelteComponent {
    $destroy() {
        destroy_component(this, 1);
        this.$destroy = noop;
    }
    $on(type, callback) {
        const callbacks = (this.$$.callbacks[type] || (this.$$.callbacks[type] = []));
        callbacks.push(callback);
        return () => {
            const index = callbacks.indexOf(callback);
            if (index !== -1)
                callbacks.splice(index, 1);
        };
    }
    $set($$props) {
        if (this.$$set && !is_empty($$props)) {
            this.$$.skip_bound = true;
            this.$$set($$props);
            this.$$.skip_bound = false;
        }
    }
}

function cubicOut(t) {
    const f = t - 1.0;
    return f * f * f + 1.0;
}

function fly(node, { delay = 0, duration = 400, easing = cubicOut, x = 0, y = 0, opacity = 0 } = {}) {
    const style = getComputedStyle(node);
    const target_opacity = +style.opacity;
    const transform = style.transform === 'none' ? '' : style.transform;
    const od = target_opacity * (1 - opacity);
    return {
        delay,
        duration,
        easing,
        css: (t, u) => `
			transform: ${transform} translate(${(1 - t) * x}px, ${(1 - t) * y}px);
			opacity: ${target_opacity - (od * u)}`
    };
}

const subscriber_queue = [];
/**
 * Create a `Writable` store that allows both updating and reading by subscription.
 * @param {*=}value initial value
 * @param {StartStopNotifier=}start start and stop notifications for subscriptions
 */
function writable(value, start = noop) {
    let stop;
    const subscribers = new Set();
    function set(new_value) {
        if (not_equal(value, new_value)) {
            value = new_value;
            if (stop) { // store is ready
                const run_queue = !subscriber_queue.length;
                for (const subscriber of subscribers) {
                    subscriber[1]();
                    subscriber_queue.push(subscriber, value);
                }
                if (run_queue) {
                    for (let i = 0; i < subscriber_queue.length; i += 2) {
                        subscriber_queue[i][0](subscriber_queue[i + 1]);
                    }
                    subscriber_queue.length = 0;
                }
            }
        }
    }
    function update(fn) {
        set(fn(value));
    }
    function subscribe(run, invalidate = noop) {
        const subscriber = [run, invalidate];
        subscribers.add(subscriber);
        if (subscribers.size === 1) {
            stop = start(set) || noop;
        }
        run(value);
        return () => {
            subscribers.delete(subscriber);
            if (subscribers.size === 0) {
                stop();
                stop = null;
            }
        };
    }
    return { set, update, subscribe };
}

function get_interpolator(a, b) {
    if (a === b || a !== a)
        return () => a;
    const type = typeof a;
    if (Array.isArray(a)) {
        const arr = b.map((bi, i) => {
            return get_interpolator(a[i], bi);
        });
        return t => arr.map(fn => fn(t));
    }
    if (type === 'number') {
        const delta = b - a;
        return t => a + t * delta;
    }
    
}
function tweened(value, defaults = {}) {
    const store = writable(value);
    let task;
    let target_value = value;
    function set(new_value, opts) {
        if (value == null) {
            store.set(value = new_value);
            return Promise.resolve();
        }
        target_value = new_value;
        let previous_task = task;
        let started = false;
        let { delay = 0, duration = 400, easing = identity, interpolate = get_interpolator } = assign(assign({}, defaults), opts);
        if (duration === 0) {
            if (previous_task) {
                previous_task.abort();
                previous_task = null;
            }
            store.set(value = target_value);
            return Promise.resolve();
        }
        const start = now() + delay;
        let fn;
        task = loop(now => {
            if (now < start)
                return true;
            if (!started) {
                fn = interpolate(value, new_value);
                if (typeof duration === 'function')
                    duration = duration(value, new_value);
                started = true;
            }
            if (previous_task) {
                previous_task.abort();
                previous_task = null;
            }
            const elapsed = now - start;
            if (elapsed > duration) {
                store.set(value = new_value);
                return false;
            }
            // @ts-ignore
            store.set(value = fn(easing(elapsed / duration)));
            return true;
        });
        return task.promise;
    }
    return {
        set,
        update: (fn, opts) => set(fn(target_value, value), opts),
        subscribe: store.subscribe
    };
}

/** true if gallery is in the process of closing */
const closing = writable(0);

/** if user prefers reduced motion  */
const prefersReducedMotion = globalThis.matchMedia?.(
	'(prefers-reduced-motion: reduce)'
).matches;

/** default options for tweens / transitions
 * @param {number} duration
 */
const defaultTweenOptions = (duration) => ({
	easing: cubicOut,
	duration: prefersReducedMotion ? 0 : duration,
});

const getThumbBackground = (activeItem) =>
	!activeItem.thumb || `url(${activeItem.thumb})`;

/**
 * Adds attributes to the given node based on the provided object.
 *
 * @param {HTMLElement} node - The node to which attributes will be added
 * @param {Record<string, string | boolean> | string} obj - The object containing key-value pairs of attributes to be added
 */
const addAttributes = (node, obj) => {
	if (!obj) {
		return
	}
	if (typeof obj === 'string') {
		obj = JSON.parse(obj);
	}
	for (const key in obj) {
		node.setAttribute(key, obj[key]);
	}
};

/* src/components/loading.svelte generated by Svelte v3.55.1 */

function create_if_block_1$2(ctx) {
	let div;
	let div_outro;
	let current;

	return {
		c() {
			div = element("div");
			div.innerHTML = `<span class="bp-bar"></span><span class="bp-o"></span>`;
			attr(div, "class", "bp-load");
			set_style(div, "background-image", getThumbBackground(/*activeItem*/ ctx[0]));
		},
		m(target, anchor) {
			insert(target, div, anchor);
			current = true;
		},
		p(ctx, dirty) {
			if (dirty & /*activeItem*/ 1) {
				set_style(div, "background-image", getThumbBackground(/*activeItem*/ ctx[0]));
			}
		},
		i(local) {
			if (current) return;
			if (div_outro) div_outro.end(1);
			current = true;
		},
		o(local) {
			if (local) {
				div_outro = create_out_transition(div, fly, { duration: 480 });
			}

			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (detaching && div_outro) div_outro.end();
		}
	};
}

// (11:57) {#if $closing}
function create_if_block$2(ctx) {
	let div;
	let div_intro;

	return {
		c() {
			div = element("div");
			attr(div, "class", "bp-load");
			set_style(div, "background-image", getThumbBackground(/*activeItem*/ ctx[0]));
		},
		m(target, anchor) {
			insert(target, div, anchor);
		},
		p(ctx, dirty) {
			if (dirty & /*activeItem*/ 1) {
				set_style(div, "background-image", getThumbBackground(/*activeItem*/ ctx[0]));
			}
		},
		i(local) {
			if (!div_intro) {
				add_render_callback(() => {
					div_intro = create_in_transition(div, fly, { duration: 480 });
					div_intro.start();
				});
			}
		},
		o: noop,
		d(detaching) {
			if (detaching) detach(div);
		}
	};
}

function create_fragment$4(ctx) {
	let if_block0_anchor;
	let if_block1_anchor;
	let if_block0 = !/*loaded*/ ctx[1] && create_if_block_1$2(ctx);
	let if_block1 = /*$closing*/ ctx[2] && create_if_block$2(ctx);

	return {
		c() {
			if (if_block0) if_block0.c();
			if_block0_anchor = empty();
			if (if_block1) if_block1.c();
			if_block1_anchor = empty();
		},
		m(target, anchor) {
			if (if_block0) if_block0.m(target, anchor);
			insert(target, if_block0_anchor, anchor);
			if (if_block1) if_block1.m(target, anchor);
			insert(target, if_block1_anchor, anchor);
		},
		p(ctx, [dirty]) {
			if (!/*loaded*/ ctx[1]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty & /*loaded*/ 2) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_1$2(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(if_block0_anchor.parentNode, if_block0_anchor);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*$closing*/ ctx[2]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty & /*$closing*/ 4) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block$2(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(if_block1_anchor.parentNode, if_block1_anchor);
				}
			} else if (if_block1) {
				if_block1.d(1);
				if_block1 = null;
			}
		},
		i(local) {
			transition_in(if_block0);
			transition_in(if_block1);
		},
		o(local) {
			transition_out(if_block0);
		},
		d(detaching) {
			if (if_block0) if_block0.d(detaching);
			if (detaching) detach(if_block0_anchor);
			if (if_block1) if_block1.d(detaching);
			if (detaching) detach(if_block1_anchor);
		}
	};
}

function instance$4($$self, $$props, $$invalidate) {
	let $closing;
	component_subscribe($$self, closing, $$value => $$invalidate(2, $closing = $$value));
	let { activeItem } = $$props;
	let { loaded } = $$props;

	$$self.$$set = $$props => {
		if ('activeItem' in $$props) $$invalidate(0, activeItem = $$props.activeItem);
		if ('loaded' in $$props) $$invalidate(1, loaded = $$props.loaded);
	};

	return [activeItem, loaded, $closing];
}

class Loading extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$4, create_fragment$4, not_equal, { activeItem: 0, loaded: 1 });
	}
}

/* src/components/image.svelte generated by Svelte v3.55.1 */

function create_if_block_1$1(ctx) {
	let img;
	let img_sizes_value;
	let img_outro;
	let current;
	let mounted;
	let dispose;

	return {
		c() {
			img = element("img");
			attr(img, "sizes", img_sizes_value = /*opts*/ ctx[8].sizes || `${/*sizes*/ ctx[1]}px`);
			attr(img, "alt", /*activeItem*/ ctx[7].alt);
		},
		m(target, anchor) {
			insert(target, img, anchor);
			current = true;

			if (!mounted) {
				dispose = [
					action_destroyer(/*addSrc*/ ctx[21].call(null, img)),
					listen(img, "error", /*error_handler*/ ctx[27])
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (!current || dirty[0] & /*sizes*/ 2 && img_sizes_value !== (img_sizes_value = /*opts*/ ctx[8].sizes || `${/*sizes*/ ctx[1]}px`)) {
				attr(img, "sizes", img_sizes_value);
			}
		},
		i(local) {
			if (current) return;
			if (img_outro) img_outro.end(1);
			current = true;
		},
		o(local) {
			img_outro = create_out_transition(img, fly, {});
			current = false;
		},
		d(detaching) {
			if (detaching) detach(img);
			if (detaching && img_outro) img_outro.end();
			mounted = false;
			run_all(dispose);
		}
	};
}

// (384:10) {#if showLoader}
function create_if_block$1(ctx) {
	let loading;
	let current;

	loading = new Loading({
			props: {
				activeItem: /*activeItem*/ ctx[7],
				loaded: /*loaded*/ ctx[2]
			}
		});

	return {
		c() {
			create_component(loading.$$.fragment);
		},
		m(target, anchor) {
			mount_component(loading, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const loading_changes = {};
			if (dirty[0] & /*loaded*/ 4) loading_changes.loaded = /*loaded*/ ctx[2];
			loading.$set(loading_changes);
		},
		i(local) {
			if (current) return;
			transition_in(loading.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(loading.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(loading, detaching);
		}
	};
}

function create_fragment$3(ctx) {
	let div1;
	let div0;
	let if_block0_anchor;
	let style_transform = `translate3d(${/*$imageDimensions*/ ctx[0][0] / -2 + /*$zoomDragTranslate*/ ctx[6][0]}px, ${/*$imageDimensions*/ ctx[0][1] / -2 + /*$zoomDragTranslate*/ ctx[6][1]}px, 0)`;
	let current;
	let mounted;
	let dispose;
	let if_block0 = /*loaded*/ ctx[2] && create_if_block_1$1(ctx);
	let if_block1 = /*showLoader*/ ctx[3] && create_if_block$1(ctx);

	return {
		c() {
			div1 = element("div");
			div0 = element("div");
			if (if_block0) if_block0.c();
			if_block0_anchor = empty();
			if (if_block1) if_block1.c();
			attr(div0, "class", "bp-img");
			set_style(div0, "width", /*$imageDimensions*/ ctx[0][0] + "px");
			set_style(div0, "height", /*$imageDimensions*/ ctx[0][1] + "px");
			toggle_class(div0, "bp-drag", /*pointerDown*/ ctx[4]);
			toggle_class(div0, "bp-canzoom", /*maxZoom*/ ctx[11] > 1 && /*$imageDimensions*/ ctx[0][0] < /*naturalWidth*/ ctx[12]);
			set_style(div0, "background-image", getThumbBackground(/*activeItem*/ ctx[7]));
			set_style(div0, "transform", style_transform);
			attr(div1, "class", "bp-img-wrap");
			toggle_class(div1, "bp-close", /*closingWhileZoomed*/ ctx[5]);
		},
		m(target, anchor) {
			insert(target, div1, anchor);
			append(div1, div0);
			if (if_block0) if_block0.m(div0, null);
			append(div0, if_block0_anchor);
			if (if_block1) if_block1.m(div0, null);
			current = true;

			if (!mounted) {
				dispose = [
					action_destroyer(/*onMount*/ ctx[20].call(null, div0)),
					listen(div1, "wheel", /*onWheel*/ ctx[15]),
					listen(div1, "pointerdown", /*onPointerDown*/ ctx[16]),
					listen(div1, "pointermove", /*onPointerMove*/ ctx[17]),
					listen(div1, "pointerup", /*onPointerUp*/ ctx[19]),
					listen(div1, "pointercancel", /*removeEventFromCache*/ ctx[18])
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (/*loaded*/ ctx[2]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);

					if (dirty[0] & /*loaded*/ 4) {
						transition_in(if_block0, 1);
					}
				} else {
					if_block0 = create_if_block_1$1(ctx);
					if_block0.c();
					transition_in(if_block0, 1);
					if_block0.m(div0, if_block0_anchor);
				}
			} else if (if_block0) {
				group_outros();

				transition_out(if_block0, 1, 1, () => {
					if_block0 = null;
				});

				check_outros();
			}

			if (/*showLoader*/ ctx[3]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty[0] & /*showLoader*/ 8) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block$1(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(div0, null);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}

			if (!current || dirty[0] & /*$imageDimensions*/ 1) {
				set_style(div0, "width", /*$imageDimensions*/ ctx[0][0] + "px");
			}

			if (!current || dirty[0] & /*$imageDimensions*/ 1) {
				set_style(div0, "height", /*$imageDimensions*/ ctx[0][1] + "px");
			}

			if (!current || dirty[0] & /*pointerDown*/ 16) {
				toggle_class(div0, "bp-drag", /*pointerDown*/ ctx[4]);
			}

			if (!current || dirty[0] & /*maxZoom, $imageDimensions, naturalWidth*/ 6145) {
				toggle_class(div0, "bp-canzoom", /*maxZoom*/ ctx[11] > 1 && /*$imageDimensions*/ ctx[0][0] < /*naturalWidth*/ ctx[12]);
			}

			if (dirty[0] & /*$imageDimensions, $zoomDragTranslate*/ 65 && style_transform !== (style_transform = `translate3d(${/*$imageDimensions*/ ctx[0][0] / -2 + /*$zoomDragTranslate*/ ctx[6][0]}px, ${/*$imageDimensions*/ ctx[0][1] / -2 + /*$zoomDragTranslate*/ ctx[6][1]}px, 0)`)) {
				set_style(div0, "transform", style_transform);
			}

			if (!current || dirty[0] & /*closingWhileZoomed*/ 32) {
				toggle_class(div1, "bp-close", /*closingWhileZoomed*/ ctx[5]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);
			transition_in(if_block1);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			transition_out(if_block1);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div1);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
			mounted = false;
			run_all(dispose);
		}
	};
}

function instance$3($$self, $$props, $$invalidate) {
	let $zoomed;
	let $zoomDragTranslate;
	let $closing;
	let $imageDimensions;
	component_subscribe($$self, closing, $$value => $$invalidate(26, $closing = $$value));
	let { props } = $$props;
	let { smallScreen } = $$props;
	let { activeItem, opts, prev, next, zoomed, container } = props;
	component_subscribe($$self, zoomed, value => $$invalidate(25, $zoomed = value));
	let maxZoom = activeItem.maxZoom || opts.maxZoom || 10;
	let calculatedDimensions = props.calculateDimensions(activeItem);

	/** value of sizes attribute */
	let sizes = calculatedDimensions[0];

	/** tracks load state of image */
	let loaded, showLoader;

	/** stores pinch info if multiple touch events active */
	let pinchDetails;

	/** image html element (.bp-img) */
	let bpImg;

	/** track distance for pinch events */
	let prevDiff = 0;

	let pointerDown, hasDragged;
	let dragStartX, dragStartY;

	/** zoomDragTranslate values on start of drag */
	let dragStartTranslateX, dragStartTranslateY;

	/** if true, adds class to .bp-wrap to avoid image cropping */
	let closingWhileZoomed;

	const naturalWidth = +activeItem.width;

	/** store positions for drag inertia */
	const dragPositions = [];

	/** cache pointer events to handle pinch */
	const pointerCache = new Map();

	/** tween to control image size */
	const imageDimensions = tweened(calculatedDimensions, defaultTweenOptions(400));

	component_subscribe($$self, imageDimensions, value => $$invalidate(0, $imageDimensions = value));

	/** translate transform for pointerDown */
	const zoomDragTranslate = tweened([0, 0], defaultTweenOptions(400));

	component_subscribe($$self, zoomDragTranslate, value => $$invalidate(6, $zoomDragTranslate = value));

	/** calculate translate position with bounds */
	const boundTranslateValues = ([x, y], newDimensions = $imageDimensions) => {
		// image drag translate bounds
		const maxTranslateX = (newDimensions[0] - container.w) / 2;

		const maxTranslateY = (newDimensions[1] - container.h) / 2;

		// x max drag
		if (maxTranslateX < 0) {
			x = 0;
		} else if (x > maxTranslateX) {
			if (smallScreen) {
				// bound to left side (allow slight over drag)
				x = pointerDown
				? maxTranslateX + (x - maxTranslateX) / 10
				: maxTranslateX;

				// previous item if dragged past threshold
				if (x > maxTranslateX + 20) {
					// pointerdown = undefined to stop pointermove from running again
					$$invalidate(4, pointerDown = prev());
				}
			} else {
				x = maxTranslateX;
			}
		} else if (x < -maxTranslateX) {
			// bound to right side (allow slight over drag)
			if (smallScreen) {
				x = pointerDown
				? -maxTranslateX - (-maxTranslateX - x) / 10
				: -maxTranslateX;

				// next item if dragged past threshold
				if (x < -maxTranslateX - 20) {
					// pointerdown = undefined to stop pointermove from running again
					$$invalidate(4, pointerDown = next());
				}
			} else {
				x = -maxTranslateX;
			}
		}

		// y max drag
		if (maxTranslateY < 0) {
			y = 0;
		} else if (y > maxTranslateY) {
			y = maxTranslateY;
		} else if (y < -maxTranslateY) {
			y = -maxTranslateY;
		}

		return [x, y];
	};

	/** updates zoom level in or out based on amt value */
	function changeZoom(amt = maxZoom, e) {
		if ($closing) {
			return;
		}

		const maxWidth = calculatedDimensions[0] * maxZoom;
		let newWidth = $imageDimensions[0] + $imageDimensions[0] * amt;
		let newHeight = $imageDimensions[1] + $imageDimensions[1] * amt;

		if (amt > 0) {
			if (newWidth > maxWidth) {
				// requesting size large than max zoom
				newWidth = maxWidth;

				newHeight = calculatedDimensions[1] * maxZoom;
			}

			if (newWidth > naturalWidth) {
				// if requesting zoom larger than natural size
				newWidth = naturalWidth;

				newHeight = +activeItem.height;
			}
		} else if (newWidth < calculatedDimensions[0]) {
			// if requesting image smaller than starting size
			imageDimensions.set(calculatedDimensions);

			return zoomDragTranslate.set([0, 0]);
		}

		let { x, y, width, height } = bpImg.getBoundingClientRect();

		// distance clicked from center of image
		const offsetX = e ? e.clientX - x - width / 2 : 0;

		const offsetY = e ? e.clientY - y - height / 2 : 0;
		x = -offsetX * (newWidth / width) + offsetX;
		y = -offsetY * (newHeight / height) + offsetY;
		const newDimensions = [newWidth, newHeight];

		// set new dimensions and update sizes property
		imageDimensions.set(newDimensions).then(() => {
			$$invalidate(1, sizes = Math.round(Math.max(sizes, newWidth)));
		});

		// update translate value
		zoomDragTranslate.set(boundTranslateValues([$zoomDragTranslate[0] + x, $zoomDragTranslate[1] + y], newDimensions));
	}

	// allow zoom to be read / set externally
	Object.defineProperty(activeItem, 'zoom', {
		configurable: true,
		get: () => $zoomed,
		set: bool => changeZoom(bool ? maxZoom : -maxZoom)
	});

	const onWheel = e => {
		// return if scrolling past inline gallery w/ wheel
		if (opts.inline && !$zoomed) {
			return;
		}

		// preventDefault to stop scrolling on zoomed inline image
		e.preventDefault();

		// change zoom on wheel
		changeZoom(e.deltaY / -300, e);
	};

	/** on drag start, store initial position and image translate values */
	const onPointerDown = e => {
		// don't run if right click
		if (e.button !== 2) {
			e.preventDefault();
			$$invalidate(4, pointerDown = true);
			pointerCache.set(e.pointerId, e);
			dragStartX = e.clientX;
			dragStartY = e.clientY;
			dragStartTranslateX = $zoomDragTranslate[0];
			dragStartTranslateY = $zoomDragTranslate[1];
		}
	};

	/** on drag, update image translate val */
	const onPointerMove = e => {
		if (pointerCache.size > 1) {
			// if multiple pointer events, pass to handlePinch function
			$$invalidate(4, pointerDown = false);

			return opts.noPinch?.(container.el) || handlePinch(e);
		}

		if (!pointerDown) {
			return;
		}

		let x = e.clientX;
		let y = e.clientY;

		// store positions for drag inertia, flag drag by distance 
		dragPositions.push({ x, y });

		hasDragged ||= Math.hypot(x - dragStartX, y - dragStartY) > 5;

		// overall drag diff from start location
		x = x - dragStartX;

		y = y - dragStartY;

		// handle unzoomed left / right / up swipes
		if (!$zoomed) {
			// close if swipe up
			if (y < -90) {
				$$invalidate(4, pointerDown = !opts.noClose && props.close());
			}

			// only handle left / right if not swiping vertically
			if (Math.abs(y) < 30) {
				// previous if swipe left
				if (x > 40) {
					// pointerdown = undefined to stop pointermove from running again
					$$invalidate(4, pointerDown = prev());
				}

				// next if swipe right
				if (x < -40) {
					// pointerdown = undefined to stop pointermove from running again
					$$invalidate(4, pointerDown = next());
				}
			}
		}

		// image drag when zoomed
		if ($zoomed && hasDragged && !$closing) {
			zoomDragTranslate.set(boundTranslateValues([dragStartTranslateX + x, dragStartTranslateY + y]), { duration: 0 });
		}
	};

	const handlePinch = e => {
		// update event in cache and get values
		const [p1, p2] = pointerCache.set(e.pointerId, e).values();

		// Calculate the distance between the two pointers
		const dx = p1.clientX - p2.clientX;

		const dy = p1.clientY - p2.clientY;
		const curDiff = Math.hypot(dx, dy);

		// cache the original pinch center
		pinchDetails = pinchDetails || {
			clientX: (p1.clientX + p2.clientX) / 2,
			clientY: (p1.clientY + p2.clientY) / 2
		};

		// scale image
		changeZoom(((prevDiff || curDiff) - curDiff) / -35, pinchDetails);

		// Cache the distance for the next move event
		prevDiff = curDiff;
	};

	/** remove event from pointer event cache */
	const removeEventFromCache = e => pointerCache.delete(e.pointerId);

	function onPointerUp(e) {
		removeEventFromCache(e);

		if (pinchDetails) {
			// reset prevDiff and clear pointerDown to trigger return below
			$$invalidate(4, pointerDown = prevDiff = 0);

			// set pinchDetails to null after last finger lifts
			pinchDetails = pointerCache.size ? pinchDetails : null;
		}

		// make sure pointer events don't carry over to next image
		if (!pointerDown) {
			return;
		}

		$$invalidate(4, pointerDown = false);

		// close if overlay is clicked
		if (e.target === this && !opts.noClose) {
			return props.close();
		}

		// add drag inertia / snap back to bounds
		if (hasDragged) {
			const [posOne, posTwo, posThree] = dragPositions.slice(-3);
			const xDiff = posTwo.x - posThree.x;
			const yDiff = posTwo.y - posThree.y;

			if (Math.hypot(xDiff, yDiff) > 5) {
				zoomDragTranslate.set(boundTranslateValues([
					$zoomDragTranslate[0] - (posOne.x - posThree.x) * 5,
					$zoomDragTranslate[1] - (posOne.y - posThree.y) * 5
				]));
			}
		} else if (!opts.onImageClick?.(container.el, activeItem)) {
			changeZoom($zoomed ? -maxZoom : maxZoom, e);
		}

		// reset pointer states
		hasDragged = false;

		// reset dragPositions
		dragPositions.length = 0;
	}

	const onMount = node => {
		bpImg = node;

		// handle globalThis resize
		props.setResizeFunc(() => {
			$$invalidate(24, calculatedDimensions = props.calculateDimensions(activeItem));

			// adjust image size / zoom on resize, but not on mobile because
			// some browsers (ios safari 15) constantly resize screen on drag
			if (opts.inline || !smallScreen) {
				imageDimensions.set(calculatedDimensions);
				zoomDragTranslate.set([0, 0]);
			}
		});

		// decode initial image before rendering
		props.loadImage(activeItem).then(() => {
			$$invalidate(2, loaded = true);
			props.preloadNext();
		});

		// show loading indicator if needed
		setTimeout(
			() => {
				$$invalidate(3, showLoader = !loaded);
			},
			250
		);
	};

	const addSrc = node => {
		addAttributes(node, activeItem.attr);
		node.srcset = activeItem.img;
	};

	const error_handler = error => opts.onError?.(container, activeItem, error);

	$$self.$$set = $$props => {
		
		if ('smallScreen' in $$props) $$invalidate(23, smallScreen = $$props.smallScreen);
	};

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*$imageDimensions, calculatedDimensions*/ 16777217) {
			zoomed.set($imageDimensions[0] - 10 > calculatedDimensions[0]);
		}

		if ($$self.$$.dirty[0] & /*$closing, $zoomed, calculatedDimensions*/ 117440512) {
			// if zoomed while closing, zoom out image and add class
			// to change contain value on .bp-wrap to avoid cropping
			if ($closing && $zoomed && !opts.intro) {
				const closeTweenOpts = defaultTweenOptions(480);
				zoomDragTranslate.set([0, 0], closeTweenOpts);
				imageDimensions.set(calculatedDimensions, closeTweenOpts);
				$$invalidate(5, closingWhileZoomed = true);
			}
		}
	};

	return [
		$imageDimensions,
		sizes,
		loaded,
		showLoader,
		pointerDown,
		closingWhileZoomed,
		$zoomDragTranslate,
		activeItem,
		opts,
		zoomed,
		container,
		maxZoom,
		naturalWidth,
		imageDimensions,
		zoomDragTranslate,
		onWheel,
		onPointerDown,
		onPointerMove,
		removeEventFromCache,
		onPointerUp,
		onMount,
		addSrc,
		props,
		smallScreen,
		calculatedDimensions,
		$zoomed,
		$closing,
		error_handler
	];
}

class Image extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$3, create_fragment$3, not_equal, { props: 22, smallScreen: 23 }, null, [-1, -1]);
	}
}

/* src/components/iframe.svelte generated by Svelte v3.55.1 */

function create_fragment$2(ctx) {
	let div;
	let iframe;
	let loading;
	let current;
	let mounted;
	let dispose;

	loading = new Loading({
			props: {
				activeItem: /*activeItem*/ ctx[2],
				loaded: /*loaded*/ ctx[0]
			}
		});

	return {
		c() {
			div = element("div");
			iframe = element("iframe");
			create_component(loading.$$.fragment);
			attr(iframe, "allow", "autoplay; fullscreen");
			attr(iframe, "title", /*activeItem*/ ctx[2].title);
			attr(div, "class", "bp-if");
			set_style(div, "width", /*dimensions*/ ctx[1][0] + "px");
			set_style(div, "height", /*dimensions*/ ctx[1][1] + "px");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, iframe);
			mount_component(loading, div, null);
			current = true;

			if (!mounted) {
				dispose = [
					action_destroyer(/*addSrc*/ ctx[3].call(null, iframe)),
					listen(iframe, "load", /*load_handler*/ ctx[5])
				];

				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			const loading_changes = {};
			if (dirty & /*loaded*/ 1) loading_changes.loaded = /*loaded*/ ctx[0];
			loading.$set(loading_changes);

			if (!current || dirty & /*dimensions*/ 2) {
				set_style(div, "width", /*dimensions*/ ctx[1][0] + "px");
			}

			if (!current || dirty & /*dimensions*/ 2) {
				set_style(div, "height", /*dimensions*/ ctx[1][1] + "px");
			}
		},
		i(local) {
			if (current) return;
			transition_in(loading.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(loading.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(loading);
			mounted = false;
			run_all(dispose);
		}
	};
}

function instance$2($$self, $$props, $$invalidate) {
	let { props } = $$props;
	let loaded, dimensions;
	const { activeItem } = props;
	const setDimensions = () => $$invalidate(1, dimensions = props.calculateDimensions(activeItem));
	setDimensions();
	props.setResizeFunc(setDimensions);

	const addSrc = node => {
		addAttributes(node, activeItem.attr);
		node.src = activeItem.iframe;
	};

	const load_handler = () => $$invalidate(0, loaded = true);

	

	return [loaded, dimensions, activeItem, addSrc, props, load_handler];
}

class Iframe extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$2, create_fragment$2, not_equal, { props: 4 });
	}
}

/* src/components/video.svelte generated by Svelte v3.55.1 */

function create_fragment$1(ctx) {
	let div;
	let loading;
	let current;
	let mounted;
	let dispose;

	loading = new Loading({
			props: {
				activeItem: /*activeItem*/ ctx[2],
				loaded: /*loaded*/ ctx[0]
			}
		});

	return {
		c() {
			div = element("div");
			create_component(loading.$$.fragment);
			attr(div, "class", "bp-vid");
			set_style(div, "width", /*dimensions*/ ctx[1][0] + "px");
			set_style(div, "height", /*dimensions*/ ctx[1][1] + "px");
			set_style(div, "background-image", getThumbBackground(/*activeItem*/ ctx[2]));
		},
		m(target, anchor) {
			insert(target, div, anchor);
			mount_component(loading, div, null);
			current = true;

			if (!mounted) {
				dispose = action_destroyer(/*onMount*/ ctx[3].call(null, div));
				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			const loading_changes = {};
			if (dirty & /*loaded*/ 1) loading_changes.loaded = /*loaded*/ ctx[0];
			loading.$set(loading_changes);

			if (!current || dirty & /*dimensions*/ 2) {
				set_style(div, "width", /*dimensions*/ ctx[1][0] + "px");
			}

			if (!current || dirty & /*dimensions*/ 2) {
				set_style(div, "height", /*dimensions*/ ctx[1][1] + "px");
			}
		},
		i(local) {
			if (current) return;
			transition_in(loading.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(loading.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(loading);
			mounted = false;
			dispose();
		}
	};
}

function instance$1($$self, $$props, $$invalidate) {
	let { props } = $$props;
	let loaded, dimensions;
	const { activeItem, opts, container } = props;
	const setDimensions = () => $$invalidate(1, dimensions = props.calculateDimensions(activeItem));
	setDimensions();
	props.setResizeFunc(setDimensions);

	/** create audo / video element */
	const onMount = node => {
		let mediaElement;

		/** takes supplied object and creates elements in video */
		const appendToVideo = (tag, arr) => {
			if (!Array.isArray(arr)) {
				arr = JSON.parse(arr);
			}

			for (const obj of arr) {
				// create media element if it doesn't exist
				if (!mediaElement) {
					mediaElement = document.createElement((obj.type?.includes('audio')) ? 'audio' : 'video');

					addAttributes(mediaElement, {
						controls: true,
						autoplay: true,
						playsinline: true,
						tabindex: '0'
					});

					addAttributes(mediaElement, activeItem.attr);
				}

				// add sources / tracks to media element
				const el = document.createElement(tag);

				addAttributes(el, obj);

				if (tag == 'source') {
					el.onError = error => opts.onError?.(container, activeItem, error);
				}

				mediaElement.append(el);
			}
		};

		appendToVideo('source', activeItem.sources);
		appendToVideo('track', activeItem.tracks || []);
		mediaElement.oncanplay = () => $$invalidate(0, loaded = true);
		node.append(mediaElement);
	};

	

	return [loaded, dimensions, activeItem, onMount, props];
}

class Video extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$1, create_fragment$1, not_equal, { props: 4 });
	}
}

/* src/bigger-picture.svelte generated by Svelte v3.55.1 */

function create_if_block(ctx) {
	let div2;
	let div0;
	let div0_outro;
	let previous_key = /*activeItem*/ ctx[6].i;
	let div1;
	let button;
	let div1_outro;
	let current;
	let mounted;
	let dispose;
	let key_block = create_key_block(ctx);
	let if_block = /*items*/ ctx[0].length > 1 && create_if_block_1(ctx);

	return {
		c() {
			div2 = element("div");
			div0 = element("div");
			key_block.c();
			div1 = element("div");
			button = element("button");
			if (if_block) if_block.c();
			attr(button, "class", "bp-x");
			attr(button, "title", "Close");
			attr(button, "aria-label", "Close");
			attr(div1, "class", "bp-controls");
			attr(div2, "class", "bp-wrap");
			toggle_class(div2, "bp-zoomed", /*$zoomed*/ ctx[10]);
			toggle_class(div2, "bp-inline", /*inline*/ ctx[8]);
			toggle_class(div2, "bp-small", /*smallScreen*/ ctx[7]);
			toggle_class(div2, "bp-noclose", /*opts*/ ctx[5].noClose);
		},
		m(target, anchor) {
			insert(target, div2, anchor);
			append(div2, div0);
			key_block.m(div2, null);
			append(div2, div1);
			append(div1, button);
			if (if_block) if_block.m(div1, null);
			current = true;

			if (!mounted) {
				dispose = [
					listen(button, "click", /*close*/ ctx[1]),
					action_destroyer(/*containerActions*/ ctx[14].call(null, div2))
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (dirty[0] & /*activeItem*/ 64 && not_equal(previous_key, previous_key = /*activeItem*/ ctx[6].i)) {
				group_outros();
				transition_out(key_block, 1, 1, noop);
				check_outros();
				key_block = create_key_block(ctx);
				key_block.c();
				transition_in(key_block, 1);
				key_block.m(div2, div1);
			} else {
				key_block.p(ctx, dirty);
			}

			if (/*items*/ ctx[0].length > 1) {
				if (if_block) {
					if_block.p(ctx, dirty);
				} else {
					if_block = create_if_block_1(ctx);
					if_block.c();
					if_block.m(div1, null);
				}
			} else if (if_block) {
				if_block.d(1);
				if_block = null;
			}

			if (!current || dirty[0] & /*$zoomed*/ 1024) {
				toggle_class(div2, "bp-zoomed", /*$zoomed*/ ctx[10]);
			}

			if (!current || dirty[0] & /*inline*/ 256) {
				toggle_class(div2, "bp-inline", /*inline*/ ctx[8]);
			}

			if (!current || dirty[0] & /*smallScreen*/ 128) {
				toggle_class(div2, "bp-small", /*smallScreen*/ ctx[7]);
			}

			if (!current || dirty[0] & /*opts*/ 32) {
				toggle_class(div2, "bp-noclose", /*opts*/ ctx[5].noClose);
			}
		},
		i(local) {
			if (current) return;
			if (div0_outro) div0_outro.end(1);
			transition_in(key_block);
			if (div1_outro) div1_outro.end(1);
			current = true;
		},
		o(local) {
			if (local) {
				div0_outro = create_out_transition(div0, fly, { duration: 480 });
			}

			transition_out(key_block);

			if (local) {
				div1_outro = create_out_transition(div1, fly, {});
			}

			current = false;
		},
		d(detaching) {
			if (detaching) detach(div2);
			if (detaching && div0_outro) div0_outro.end();
			key_block.d(detaching);
			if (if_block) if_block.d();
			if (detaching && div1_outro) div1_outro.end();
			mounted = false;
			run_all(dispose);
		}
	};
}

// (311:199) {:else}
function create_else_block(ctx) {
	let div;
	let raw_value = (/*activeItem*/ ctx[6].html ?? /*activeItem*/ ctx[6].element.outerHTML) + "";

	return {
		c() {
			div = element("div");
			attr(div, "class", "bp-html");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			div.innerHTML = raw_value;
		},
		p(ctx, dirty) {
			if (dirty[0] & /*activeItem*/ 64 && raw_value !== (raw_value = (/*activeItem*/ ctx[6].html ?? /*activeItem*/ ctx[6].element.outerHTML) + "")) div.innerHTML = raw_value;		},
		i: noop,
		o: noop,
		d(detaching) {
			if (detaching) detach(div);
		}
	};
}

// (311:165) 
function create_if_block_5(ctx) {
	let iframe;
	let current;

	iframe = new Iframe({
			props: { props: /*getChildProps*/ ctx[13]() }
		});

	return {
		c() {
			create_component(iframe.$$.fragment);
		},
		m(target, anchor) {
			mount_component(iframe, target, anchor);
			current = true;
		},
		p: noop,
		i(local) {
			if (current) return;
			transition_in(iframe.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(iframe.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(iframe, detaching);
		}
	};
}

// (311:104) 
function create_if_block_4(ctx) {
	let video;
	let current;

	video = new Video({
			props: { props: /*getChildProps*/ ctx[13]() }
		});

	return {
		c() {
			create_component(video.$$.fragment);
		},
		m(target, anchor) {
			mount_component(video, target, anchor);
			current = true;
		},
		p: noop,
		i(local) {
			if (current) return;
			transition_in(video.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(video.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(video, detaching);
		}
	};
}

// (311:4) {#if activeItem.img}
function create_if_block_3(ctx) {
	let imageitem;
	let current;

	imageitem = new Image({
			props: {
				props: /*getChildProps*/ ctx[13](),
				smallScreen: /*smallScreen*/ ctx[7]
			}
		});

	return {
		c() {
			create_component(imageitem.$$.fragment);
		},
		m(target, anchor) {
			mount_component(imageitem, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const imageitem_changes = {};
			if (dirty[0] & /*smallScreen*/ 128) imageitem_changes.smallScreen = /*smallScreen*/ ctx[7];
			imageitem.$set(imageitem_changes);
		},
		i(local) {
			if (current) return;
			transition_in(imageitem.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(imageitem.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(imageitem, detaching);
		}
	};
}

// (311:299) {#if activeItem.caption}
function create_if_block_2(ctx) {
	let div;
	let raw_value = /*activeItem*/ ctx[6].caption + "";
	let div_outro;
	let current;

	return {
		c() {
			div = element("div");
			attr(div, "class", "bp-cap");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			div.innerHTML = raw_value;
			current = true;
		},
		p(ctx, dirty) {
			if ((!current || dirty[0] & /*activeItem*/ 64) && raw_value !== (raw_value = /*activeItem*/ ctx[6].caption + "")) div.innerHTML = raw_value;		},
		i(local) {
			if (current) return;
			if (div_outro) div_outro.end(1);
			current = true;
		},
		o(local) {
			div_outro = create_out_transition(div, fly, { duration: 200 });
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if (detaching && div_outro) div_outro.end();
		}
	};
}

// (300:43) {#key activeItem.i}
function create_key_block(ctx) {
	let div;
	let current_block_type_index;
	let if_block0;
	let div_intro;
	let div_outro;
	let if_block1_anchor;
	let current;
	let mounted;
	let dispose;
	const if_block_creators = [create_if_block_3, create_if_block_4, create_if_block_5, create_else_block];
	const if_blocks = [];

	function select_block_type(ctx, dirty) {
		if (/*activeItem*/ ctx[6].img) return 0;
		if (/*activeItem*/ ctx[6].sources) return 1;
		if (/*activeItem*/ ctx[6].iframe) return 2;
		return 3;
	}

	current_block_type_index = select_block_type(ctx);
	if_block0 = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);
	let if_block1 = /*activeItem*/ ctx[6].caption && create_if_block_2(ctx);

	return {
		c() {
			div = element("div");
			if_block0.c();
			if (if_block1) if_block1.c();
			if_block1_anchor = empty();
			attr(div, "class", "bp-inner");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			if_blocks[current_block_type_index].m(div, null);
			if (if_block1) if_block1.m(target, anchor);
			insert(target, if_block1_anchor, anchor);
			current = true;

			if (!mounted) {
				dispose = [
					listen(div, "pointerdown", /*pointerdown_handler*/ ctx[20]),
					listen(div, "pointerup", /*pointerup_handler*/ ctx[21])
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			let previous_block_index = current_block_type_index;
			current_block_type_index = select_block_type(ctx);

			if (current_block_type_index === previous_block_index) {
				if_blocks[current_block_type_index].p(ctx, dirty);
			} else {
				group_outros();

				transition_out(if_blocks[previous_block_index], 1, 1, () => {
					if_blocks[previous_block_index] = null;
				});

				check_outros();
				if_block0 = if_blocks[current_block_type_index];

				if (!if_block0) {
					if_block0 = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);
					if_block0.c();
				} else {
					if_block0.p(ctx, dirty);
				}

				transition_in(if_block0, 1);
				if_block0.m(div, null);
			}

			if (/*activeItem*/ ctx[6].caption) {
				if (if_block1) {
					if_block1.p(ctx, dirty);

					if (dirty[0] & /*activeItem*/ 64) {
						transition_in(if_block1, 1);
					}
				} else {
					if_block1 = create_if_block_2(ctx);
					if_block1.c();
					transition_in(if_block1, 1);
					if_block1.m(if_block1_anchor.parentNode, if_block1_anchor);
				}
			} else if (if_block1) {
				group_outros();

				transition_out(if_block1, 1, 1, () => {
					if_block1 = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block0);

			add_render_callback(() => {
				if (div_outro) div_outro.end(1);
				div_intro = create_in_transition(div, /*mediaTransition*/ ctx[12], true);
				div_intro.start();
			});

			transition_in(if_block1);
			current = true;
		},
		o(local) {
			transition_out(if_block0);
			if (div_intro) div_intro.invalidate();
			div_outro = create_out_transition(div, /*mediaTransition*/ ctx[12], false);
			transition_out(if_block1);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if_blocks[current_block_type_index].d();
			if (detaching && div_outro) div_outro.end();
			if (if_block1) if_block1.d(detaching);
			if (detaching) detach(if_block1_anchor);
			mounted = false;
			run_all(dispose);
		}
	};
}

// (311:554) {#if items.length > 1}
function create_if_block_1(ctx) {
	let div;
	let raw_value = `${/*position*/ ctx[4] + 1} / ${/*items*/ ctx[0].length}` + "";
	let button0;
	let button1;
	let mounted;
	let dispose;

	return {
		c() {
			div = element("div");
			button0 = element("button");
			button1 = element("button");
			attr(div, "class", "bp-count");
			attr(button0, "class", "bp-prev");
			attr(button0, "title", "Previous");
			attr(button0, "aria-label", "Previous");
			attr(button1, "class", "bp-next");
			attr(button1, "title", "Next");
			attr(button1, "aria-label", "Next");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			div.innerHTML = raw_value;
			insert(target, button0, anchor);
			insert(target, button1, anchor);

			if (!mounted) {
				dispose = [
					listen(button0, "click", /*prev*/ ctx[2]),
					listen(button1, "click", /*next*/ ctx[3])
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (dirty[0] & /*position, items*/ 17 && raw_value !== (raw_value = `${/*position*/ ctx[4] + 1} / ${/*items*/ ctx[0].length}` + "")) div.innerHTML = raw_value;		},
		d(detaching) {
			if (detaching) detach(div);
			if (detaching) detach(button0);
			if (detaching) detach(button1);
			mounted = false;
			run_all(dispose);
		}
	};
}

function create_fragment(ctx) {
	let if_block_anchor;
	let current;
	let if_block = /*items*/ ctx[0] && create_if_block(ctx);

	return {
		c() {
			if (if_block) if_block.c();
			if_block_anchor = empty();
		},
		m(target, anchor) {
			if (if_block) if_block.m(target, anchor);
			insert(target, if_block_anchor, anchor);
			current = true;
		},
		p(ctx, dirty) {
			if (/*items*/ ctx[0]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[0] & /*items*/ 1) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(if_block_anchor.parentNode, if_block_anchor);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block);
			current = true;
		},
		o(local) {
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (if_block) if_block.d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

function instance($$self, $$props, $$invalidate) {
	let $zoomed;
	let { items = undefined } = $$props;
	let { target = undefined } = $$props;
	const html = document.documentElement;

	/** index of current active item */
	let position;

	/** options passed via open method */
	let opts;

	/** bool tracks open state */
	let isOpen;

	/** dom element to restore focus to on close */
	let focusTrigger;

	/** bool true if container width < 769 */
	let smallScreen;

	/** bool value of inline option passed in open method */
	let inline;

	/** when position is set */
	let movement;

	/** stores target on pointerdown (ref for overlay close) */
	let clickedEl;

	/** active item object */
	let activeItem;

	/** returns true if `activeItem` is html */
	const activeItemIsHtml = () => !activeItem.img && !activeItem.sources && !activeItem.iframe;

	/** function set by child component to run when container resized */
	let resizeFunc;

	/** used by child components to set resize function */
	const setResizeFunc = fn => resizeFunc = fn;

	/** container element (el) / width (w) / height (h) */
	const container = {};

	// /** true if image is currently zoomed past starting size */
	const zoomed = writable(0);

	component_subscribe($$self, zoomed, value => $$invalidate(10, $zoomed = value));

	const open = options => {
		$$invalidate(5, opts = options);
		$$invalidate(8, inline = opts.inline);

		// add class to hide scroll if not inline gallery
		if (!inline && html.scrollHeight > html.clientHeight) {
			html.classList.add('bp-lock');
		}

		// update trigger element to restore focus
		focusTrigger = document.activeElement;

		$$invalidate(19, container.w = target.offsetWidth, container);

		$$invalidate(
			19,
			container.h = target === document.body
			? globalThis.innerHeight
			: target.clientHeight,
			container
		);

		$$invalidate(7, smallScreen = container.w < 769);
		$$invalidate(4, position = opts.position || 0);

		// set items
		$$invalidate(0, items = []);

		for (let i = 0; i < (opts.items.length || 1); i++) {
			let item = opts.items[i] || opts.items;

			if ('dataset' in item) {
				items.push({ element: item, i, ...item.dataset });
			} else {
				item.i = i;
				items.push(item);

				// set item to element for position check below
				item = item.element;
			}

			// override gallery position if needed
			if (opts.el && opts.el === item) {
				$$invalidate(4, position = i);
			}
		}
	};

	const close = () => {
		opts.onClose?.(container.el, activeItem);
		closing.set(true);
		$$invalidate(0, items = null);

		// restore focus to trigger element
		focusTrigger?.focus({ preventScroll: true });
	};

	const prev = () => setPosition(position - 1);
	const next = () => setPosition(position + 1);

	const setPosition = index => {
		movement = index - position;
		$$invalidate(4, position = getNextPosition(index));
	};

	/**
 * returns next gallery position (looped if neccessary)
 * @param {number} index
 */
	const getNextPosition = index => (index + items.length) % items.length;

	const onKeydown = e => {
		const { key, shiftKey } = e;

		if (key === 'Escape') {
			!opts.noClose && close();
		} else if (key === 'ArrowRight') {
			next();
		} else if (key === 'ArrowLeft') {
			prev();
		} else if (key === 'Tab') {
			// trap focus on tab press
			const { activeElement } = document;

			// allow browser to handle tab into video controls only
			if (shiftKey || !activeElement.controls) {
				e.preventDefault();
				const { focusWrap = container.el } = opts;
				const tabbable = [...focusWrap.querySelectorAll('*')].filter(node => node.tabIndex >= 0);
				let index = tabbable.indexOf(activeElement);
				index += tabbable.length + (shiftKey ? -1 : 1);
				tabbable[index % tabbable.length].focus();
			}
		}
	};

	/**
 * calculate dimensions of height / width resized to fit within container
 * @param {object} item object with height / width properties
 * @returns {Array} [width: number, height: number]
 */
	const calculateDimensions = ({ width = 1920, height = 1080 }) => {
		const { scale = 0.99 } = opts;
		const ratio = Math.min(1, container.w / width * scale, container.h / height * scale);

		// round number so we don't use a float as the sizes attribute
		return [Math.round(width * ratio), Math.round(height * ratio)];
	};

	/** preloads images for previous and next items in gallery */
	const preloadNext = () => {
		if (items) {
			const nextItem = items[getNextPosition(position + 1)];
			const prevItem = items[getNextPosition(position - 1)];
			!nextItem.preload && loadImage(nextItem);
			!prevItem.preload && loadImage(prevItem);
		}
	};

	/** loads / decodes image for item */
	const loadImage = item => {
		if (item.img) {
			const image = document.createElement('img');
			image.sizes = opts.sizes || `${calculateDimensions(item)[0]}px`;
			image.srcset = item.img;
			item.preload = true;

			return image.decode().catch(error => {
				
			});
		}
	};

	/** svelte transition to control opening / changing */
	const mediaTransition = (node, isEntering) => {
		if (!isOpen || !items) {
			// entrance / exit transition
			$$invalidate(18, isOpen = isEntering);

			return opts.intro
			? fly(node, { y: isEntering ? 10 : -10 })
			: scaleIn(node);
		}

		// forward / backward transition
		return fly(node, {
			x: (movement > 0 ? 20 : -20) * (isEntering ? 1 : -1),
			duration: 250
		});
	};

	/** custom svelte transition for entrance zoom */
	const scaleIn = node => {
		let dimensions;

		if (activeItemIsHtml()) {
			const bpItem = node.firstChild.firstChild;
			dimensions = [bpItem.clientWidth, bpItem.clientHeight];
		} else {
			dimensions = calculateDimensions(activeItem);
		}

		// rect is bounding rect of trigger element
		const rect = (activeItem.element || focusTrigger).getBoundingClientRect();

		const leftOffset = rect.left - (container.w - rect.width) / 2;
		const centerTop = rect.top - (container.h - rect.height) / 2;
		const scaleWidth = rect.width / dimensions[0];
		const scaleHeight = rect.height / dimensions[1];

		return {
			duration: 480,
			easing: cubicOut,
			css: (t, u) => {
				return `transform:translate3d(${leftOffset * u}px, ${centerTop * u}px, 0) scale3d(${scaleWidth + t * (1 - scaleWidth)}, ${scaleHeight + t * (1 - scaleHeight)}, 1)`;
			}
		};
	};

	/** provides object w/ needed funcs / data to child components  */
	const getChildProps = () => ({
		activeItem,
		calculateDimensions,
		loadImage,
		preloadNext,
		opts,
		prev,
		next,
		close,
		setResizeFunc,
		zoomed,
		container
	});

	/** code to run on mount / destroy */
	const containerActions = node => {
		$$invalidate(19, container.el = node, container);
		let roActive;
		opts.onOpen?.(container.el, activeItem);

		// don't use keyboard events for inline galleries
		if (!inline) {
			globalThis.addEventListener('keydown', onKeydown);
		}

		// set up resize observer
		const ro = new ResizeObserver(entries => {
				// use roActive to avoid running on initial open
				if (roActive) {
					$$invalidate(19, container.w = entries[0].contentRect.width, container);
					$$invalidate(19, container.h = entries[0].contentRect.height, container);
					$$invalidate(7, smallScreen = container.w < 769);

					// run child component resize function
					if (!activeItemIsHtml()) {
						resizeFunc?.();
					}

					// run user defined onResize function
					opts.onResize?.(container.el, activeItem);
				}

				roActive = true;
			});

		ro.observe(node);

		return {
			destroy() {
				ro.disconnect();
				globalThis.removeEventListener('keydown', onKeydown);
				closing.set(false);

				// remove class hiding scroll
				html.classList.remove('bp-lock');

				opts.onClosed?.();
			}
		};
	};

	const pointerdown_handler = e => $$invalidate(9, clickedEl = e.target);

	const pointerup_handler = function (e) {
		// only close if left click on self and not dragged
		if (e.button !== 2 && e.target === this && clickedEl === this) {
			!opts.noClose && close();
		}
	};

	$$self.$$set = $$props => {
		if ('items' in $$props) $$invalidate(0, items = $$props.items);
		if ('target' in $$props) $$invalidate(15, target = $$props.target);
	};

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*items, position, isOpen, opts, container, activeItem*/ 786545) {
			if (items) {
				// update active item when position changes
				$$invalidate(6, activeItem = items[position]);

				if (isOpen) {
					// run onUpdate when items updated
					opts.onUpdate?.(container.el, activeItem);
				}
			}
		}
	};

	return [
		items,
		close,
		prev,
		next,
		position,
		opts,
		activeItem,
		smallScreen,
		inline,
		clickedEl,
		$zoomed,
		zoomed,
		mediaTransition,
		getChildProps,
		containerActions,
		target,
		open,
		setPosition,
		isOpen,
		container,
		pointerdown_handler,
		pointerup_handler
	];
}

class Bigger_picture extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance,
			create_fragment,
			not_equal,
			{
				items: 0,
				target: 15,
				open: 16,
				close: 1,
				prev: 2,
				next: 3,
				setPosition: 17
			},
			null,
			[-1, -1]
		);
	}

	get items() {
		return this.$$.ctx[0];
	}



	get target() {
		return this.$$.ctx[15];
	}



	get open() {
		return this.$$.ctx[16];
	}

	get close() {
		return this.$$.ctx[1];
	}

	get prev() {
		return this.$$.ctx[2];
	}

	get next() {
		return this.$$.ctx[3];
	}

	get setPosition() {
		return this.$$.ctx[17];
	}
}

/**
 * Initializes BiggerPicture
 * @param {{target: HTMLElement}} options
 * @returns BiggerPicture instance
 */
function biggerPicture (options) {
	return new Bigger_picture({
		...options,
		props: options,
	})
}

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.imageLinks = [...this.querySelectorAll('[data-ref="galleryImageLink"]')];
    this.bp = biggerPicture({ target: document.body });
    this.imageClickHandler = this.openGallery.bind(this);
    this.videoKeydownHandler = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.currentTarget.querySelector('video-player')?.togglePlayback();
    };
  }

  connectedCallback() {
    this.initGallery();
  }

  disconnectedCallback() {
    // Remove event listeners
    for (let link of this.imageLinks) {
      link.removeEventListener("click", this.imageClickHandler);
      link.removeEventListener("keydown", this.videoKeydownHandler);
    }

    this.bp.$destroy?.();
    this.bp = null;
  }

  initGallery() {
    // Add click listener to open BiggerPicture
    for (let link of this.imageLinks) {
      link.addEventListener("click", this.imageClickHandler);
      if (link.querySelector('video-player')) {
        link.addEventListener("keydown", this.videoKeydownHandler);
      }
    }
  }

  openGallery(event) {
    event.preventDefault();
    const videoPlayer = event.currentTarget.querySelector('video-player');
    if (videoPlayer) {
      videoPlayer.togglePlayback();
      return;
    }

    this.bp.open({
      items: this.imageLinks,
      el: event.currentTarget,
      intro: 'fadeUp'
    });
  }
}

/*
  FilterButton adds a url query param to
  maintain filter drawer open state
*/

class FilterCheckbox extends HTMLElement {
  constructor() {
    super();
    this.checkbox = this.querySelector('[data-ref="filterDrawerCheckbox"]');
    this.filterCheckbox = [...this.querySelectorAll('[data-ref="filter-value-checkbox"]')];
    this.handleState = this.toggleQueryParam.bind(this);
    this.handleFilterCheckboxes = this.reloadGraphene.bind(this);
  }

  connectedCallback() {
    if (this.checkbox) {
      this.checkbox.addEventListener('change', this.handleState);
    }

    this.addGrapheneListeners();
  }

  disconnectedCallback() {
    if (this.checkbox && this.handleState) {
      this.checkbox.removeEventListener('change', this.handleState);
    }
    this.handleState = null;
  }

  toggleQueryParam(event) {
      const urlParams = new URLSearchParams(location.search);
      if (!this.checkbox.checked) {
        // Remove query param
        urlParams.delete('drawer');
        window.location.search = urlParams;
      }
  }

  addGrapheneListeners() {
    for (let checkbox of this.filterCheckbox) {
      checkbox.addEventListener('change', this.handleFilterCheckboxes);
    }
  }

  reloadGraphene(event) {
    const urlParams = new URLSearchParams(location.search);
    const drawerParam = urlParams.get('drawer');
    if (this.checkbox.checked) {
      if (drawerParam) {
        window.graphenehc.reload({url: `${event.target.value}`});
      } else {
        window.graphenehc.reload({url: `${event.target.value}&drawer=open`});
      }
    } else {
      window.graphenehc.reload({url: `${event.target.value}`});
    }
  }

}

class PriceSlider extends HTMLElement {
  constructor() {
    super();
    this.minRange = this.querySelector('[data-ref="minRange"]');
    this.maxRange = this.querySelector('[data-ref="maxRange"]');
    this.minValue = this.querySelector('[data-ref="minValue"]');
    this.maxValue = this.querySelector('[data-ref="maxValue"]');
    this.rangeTrack = this.querySelector('[data-ref="rangeTrack"]');
    this.minGap = 10;
    this.updateHandler = this.updateRange.bind(this);
    this.initialLoad = true;
  }

  connectedCallback() {
    // Event listeners for input changes
    this.minRange.addEventListener("change", this.updateHandler);
    this.maxRange.addEventListener("change", this.updateHandler);
    this.updateRange(); // Initialize
  }

  disconnectedCallback() {
    // Remove event listeners when the element is removed from the DOM
    this.minRange.removeEventListener("change", this.updateHandler);
    this.maxRange.removeEventListener("change", this.updateHandler);
  }

  updateRange() {
    let min = parseInt(this.minRange.value);
    let max = parseInt(this.maxRange.value);

    // Ensure min & max have a gap of at least `minGap`
    if (max - min < this.minGap) {
      if (event.target === this.minRange) {
        this.minRange.value = max - this.minGap;
      } else {
        this.maxRange.value = min + this.minGap;
      }
    }

    this.minValue.innerText = this.minRange.value;
    this.maxValue.innerText = this.maxRange.value;

    const rangeStart = this.minRange.value - parseInt(this.maxRange.getAttribute('min'));
    const rangeEnd = parseInt(this.maxRange.getAttribute('max')) - parseInt(this.maxRange.getAttribute('min'));

    let minPercent = (rangeStart / rangeEnd) * 100;
    let maxPercent = ((this.maxRange.value - parseInt(this.maxRange.getAttribute('min'))) / rangeEnd) * 100;
    this.rangeTrack.style.left = minPercent + "%";
    this.rangeTrack.style.right = 100 - maxPercent + "%";

    let urlParams = new URLSearchParams(window.location.search);
    urlParams.set("price", `${min}~${max}`);
    urlParams.set("drawer", `open`);
    if (!this.initialLoad) {
      graphenehc.reload({url: `${window.location.pathname}?${urlParams.toString()}`});
    }
    this.initialLoad = false;
  }
}

class CollectionListing extends HTMLElement {
  constructor() {
    super();
    this.loadMoreButton = this.querySelector('[data-ref="loadMoreButton"]');
    this.loadingSpinner = this.querySelector('[data-ref="loadingSpinner"]');
    this.handleLoadMoreClick = this.handleLoadMore.bind(this);
  }

  connectedCallback() {

    this.loadMoreButton.addEventListener('click', this.handleLoadMoreClick);

    // Initial Graphene load
    graphenehc.load({
      template: {
        id: "category",
        target: "#graphene-collection-products"
      },
      page_context: {
        category_id: "{{ collection.id }}"
      },
      success: (response) => {
        if (response.templates.category.current_page >= response.templates.category.page_count) {
          this.loadMoreButton?.classList.add('hidden');
        } else {
          this.loadMoreButton?.classList.remove('hidden');
        }
      }
    });

  }

  disconnectedCallback() {
    this.loadMoreButton.removeEventListener('click', this.handleLoadMoreClick);
  }

  handleLoadMore() {
    this.loadingSpinner?.classList.remove('hidden');
    graphenehc.load_page({
      target: {
        selector: "[data-ref='collectionProducts']",
        position: "beforeend"
      },
      track_page: true, // Recommended - if set to true then Graphene will automatically keep track of paging
      set_page_url: true, // If set to true then Graphene will modify the URL
      init: (response) => {
          // Optional - fires just before page is downloaded - e.g. show loading more
          console.log(response);
      },
      success: (response) => {
        console.log(response);
        this.loadingSpinner?.classList.add('hidden');
      },
      last_page: (response) => {
          // Optional - fires when last page is reached - e.g. hide or disable load more button
          this.loadMoreButton?.classList.add('hidden');
      }
    });
  }
}

class ProductSetItem extends HTMLElement {
  constructor() {
    super();
    this.product = JSON.parse(this.getAttribute('product'));
		this.quantitySelector = this.querySelector('quantity-selector');
		this.productPrice = this.querySelector('product-price');
    this.updateTotalPriceHandler = (event) => {
				this.updateTotalPrice(event.detail.quantity);
    };
  }

  connectedCallback() {
		if (this.quantitySelector) {
			this.quantitySelector.addEventListener('quantity-changed', this.updateTotalPriceHandler);
		}
  }

  updateTotalPrice(quantity) {
    if (this.productPrice) {
      this.productPrice.updatePrice(quantity);
      this.setAttribute('data-total-price', `${this.productPrice.price}`);
    }
    this.dispatchEvent(new CustomEvent('product-set-item-changed', {
        detail: {
          id: this.product.id,
          title: this.product.title,
          price: this.product.price,
          quantity: Number(quantity),
          available: this.product.available
        },
        bubbles: true,
        composed: true
      })
    );
  }
}

class ProductPrice extends HTMLElement {
  constructor() {
    super();
    this.totalPriceElement = this.querySelector('[data-ref="totalPrice"]');
    this.updateHandler = this.updatePrice.bind(this);
    this.singlePrice = this.getAttribute('single-price');
    this.totalPrice = this.singlePrice;
    this.displayZero = this.getAttribute('display-zero');
  }

  connectedCallback() {

  }

  get price() {
    return this.totalPrice;
  }

  updatePrice(quantity) {
    this.totalPrice = this.singlePrice * quantity;
    if (this.totalPriceElement && this.singlePrice) {
      this.totalPriceElement.innerText = `${formatMoney((this.singlePrice * quantity), '£{{amount_no_decimals}}')}`;
      if (!this.displayZero && quantity == 0) {
        this.classList.add('hidden');
      } else {
        this.classList.remove('hidden');
      }
    }
  }
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.searchInput = this.querySelector('[data-ref="searchInput"]');
    this.searchResultsElement = this.querySelector('[data-ref="resultsElement"]');
    this.searchCloseButton = this.querySelector('[data-ref="searchCloseButton"]');
    this.menuCloseInput = document.querySelector('[data-ref="menuToggle"]');
    this.handleSearchFocus = this.openResults.bind(this);
    this.handleSearchClose = this.closeResults.bind(this);
    this.handleMenuClose = this.closeResults.bind(this);
  }

  connectedCallback() {
    if (this.searchInput) {
      this.searchInput.addEventListener('focus', this.handleSearchFocus);
    }
    if (this.searchCloseButton) {
      this.searchCloseButton.addEventListener('click', this.handleSearchClose);
    }
    if (this.menuCloseInput) {
      this.menuCloseInput.addEventListener('change', this.handleMenuClose);
    }
  }

  disconnectedCallback() {
    if (this.searchInput) {
      this.searchInput.removeEventListener('focus', this.handleSearchFocus);
    }
    if (this.searchCloseButton) {
      this.searchCloseButton.removeEventListener('click', this.handleSearchClose);
    }
    if (this.menuCloseInput) {
      this.menuCloseInput.removeEventListener('change', this.handleMenuClose);
    }
  }

  openResults() {
    this.searchResultsElement.classList.remove('hidden');
  }

  closeResults() {
    this.searchResultsElement.classList.add('hidden');
  }
}

class SearchListing extends HTMLElement {
  constructor() {
    super();
    this.loadMoreButton = this.querySelector('[data-ref="loadMoreButton"]');
    this.loadingSpinner = this.querySelector('[data-ref="loadingSpinner"]');
    this.handleLoadMoreClick = this.handleLoadMore.bind(this);
    this.searchText = this.getAttribute('search-text');
  }

  connectedCallback() {

    this.loadMoreButton.addEventListener('click', this.handleLoadMoreClick);

    // Initial Graphene load
    graphenehc.load({
     template: {
        id: "search",
        target: "#Results"
      },
      page_context: {
        search_text: `${this.searchText}`
      },
      success: (response) => {
        if (response.templates.search.current_page >= response.templates.search.page_count) {
          this.loadMoreButton?.classList.add('hidden');
        } else {
          this.loadMoreButton?.classList.remove('hidden');
        }
      }
    });

  }

  disconnectedCallback() {
    this.loadMoreButton.removeEventListener('click', this.handleLoadMoreClick);
  }

  handleLoadMore() {
    this.loadingSpinner?.classList.remove('hidden');
    graphenehc.load_page({
      target: {
        selector: "[data-ref='collectionProducts']",
        position: "beforeend"
      },
      track_page: true, // Recommended - if set to true then Graphene will automatically keep track of paging
      set_page_url: true, // If set to true then Graphene will modify the URL
      init: (response) => {
          // Optional - fires just before page is downloaded - e.g. show loading more
          console.log(response);
      },
      success: (response) => {
        console.log(response);
        this.loadingSpinner?.classList.add('hidden');
      },
      last_page: (response) => {
          // Optional - fires when last page is reached - e.g. hide or disable load more button
          this.loadMoreButton?.classList.add('hidden');
      }
    });
  }
}

customElements.define('product-form', ProductForm);
customElements.define('cart-item', CartItem);
customElements.define('cart-count', CartCount);
customElements.define('countdown-timer', CountdownTimer);
customElements.define('carousel-slider', CarouselSlider);
customElements.define('featured-products', FeaturedProducts);
customElements.define('collection-categories', CollectionCategories);
customElements.define('quantity-selector', QuantitySelector);
customElements.define('video-player', VideoPlayer);
customElements.define('product-gallery', ProductGallery);
customElements.define('site-header', SiteHeader);
customElements.define('filter-checkbox', FilterCheckbox);
customElements.define('price-slider', PriceSlider);
customElements.define('collection-listing', CollectionListing);
customElements.define('product-set-item', ProductSetItem);
customElements.define('product-price', ProductPrice);
customElements.define('predictive-search', PredictiveSearch);
customElements.define('search-listing', SearchListing);
